import React, { useState, useEffect, useRef, useCallback, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksApi, getPhotoUrl } from '../services/api';
import { 
  savePendingPhoto, 
  savePendingAction, 
  getPendingPhotos, 
  syncPendingItems,
  hasPendingItems,
  isOnline
} from '../services/offlineStorage';
import { analyzePhoto, getVerdictDisplay } from '../services/photoVerification';
import { Button } from '../components/ui/button';
import GPSPermissionModal from '../components/GPSPermissionModal';
// Extracted dialog components
import {
  KeyboxPhotoDialog,
  ControlCommentDialog,
  ProblemReportDialog,
  AdminValidateDialog,
  RescheduleDialog,
  RejectDialog,
  ReassignDialog
} from '../components/task';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Error Boundary pour capturer les erreurs React
class TaskErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('TaskDetail Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col gap-4 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Erreur de chargement</h2>
            <p className="text-gray-600 mb-4">Une erreur s'est produite lors de l'affichage de cette mission.</p>
            {/* Debug info */}
            <details className="text-left bg-gray-100 p-2 rounded text-xs mb-4">
              <summary className="cursor-pointer text-gray-500">Détails techniques</summary>
              <pre className="mt-2 overflow-auto max-h-32 text-red-600">
                {this.state.error?.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-4 py-2 rounded-lg mr-2"
            >
              Réessayer
            </button>
            <button 
              onClick={() => window.history.back()} 
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg"
            >
              Retour
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  Euro,
  Camera,
  CheckCircle2,
  AlertTriangle,
  Play,
  Loader2,
  Home,
  ExternalLink,
  RotateCcw,
  XCircle,
  CalendarDays,
  MoreVertical,
  MessageSquare,
  ClipboardCheck,
  Send,
  Key,
  Building,
  Bed,
  Eye,
  WifiOff,
  CloudUpload,
  Navigation,
  Map,
  Bell,
  ImageIcon,
  Plus,
  FileText,
  Share2,
  Link,
  Copy,
  Check,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { extractErrorMessage } from '../utils/errorMessage';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Checklist items - same as backend
const CHECKLIST_ITEMS = [
  { id: "lights_off", label: "Vérifier que toutes les lumières sont bien éteintes" },
  { id: "heating_off", label: "Vérifier que les chauffages sont éteints" },
  { id: "new_sponge", label: "S'assurer qu'une éponge neuve est mise à disposition" },
  { id: "dish_soap", label: "Vérifier que le produit vaisselle est rempli" },
  { id: "toilet_paper", label: "Vérifier la présence de papier toilette" },
  { id: "welcome_kit", label: "Mettre en place le kit d'accueil (shampooing et gel douche)" },
  { id: "kitchen_trash_bags", label: "Vérifier la présence de sacs poubelle pour la cuisine" },
  { id: "bathroom_trash_bags", label: "Vérifier la présence de sacs poubelle pour la salle de bain" }
];

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isHousekeeper, user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [showProblemDialog, setShowProblemDialog] = useState(false);
  const [showPostValidationDialog, setShowPostValidationDialog] = useState(false);
  const [showEarlyCompletionDialog, setShowEarlyCompletionDialog] = useState(false);
  const [earlyCompletionInfo, setEarlyCompletionInfo] = useState(null);
  const [showKeyboxPhotoDialog, setShowKeyboxPhotoDialog] = useState(false);
  const [keyboxPhoto, setKeyboxPhoto] = useState(null);
  const [keyboxPhotoUploading, setKeyboxPhotoUploading] = useState(false);
  const keyboxPhotoInputRef = useRef(null);
  const [problemText, setProblemText] = useState('');
  const [problemPhotos, setProblemPhotos] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showPhotoPreview, setShowPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);
  const problemPhotoInputRef = useRef(null);
  
  // GPS state for photo validation
  const [currentGpsCoords, setCurrentGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  
  // Checklist state
  const [checklist, setChecklist] = useState({});
  const [checklistSaving, setChecklistSaving] = useState(false);

  // Dish soap verification state
  const [dishSoapStatus, setDishSoapStatus] = useState(null); // 'present' | 'replaced' | 'not_replaced' | null
  const [dishSoapPhoto, setDishSoapPhoto] = useState(null);   // storage path
  const [dishSoapUploading, setDishSoapUploading] = useState(false);
  const [showDishSoapDialog, setShowDishSoapDialog] = useState(false);
  const dishSoapInputRef = useRef(null);

  // Admin penalty dialog state
  const [showAdminPenaltyDialog, setShowAdminPenaltyDialog] = useState(false);
  const [adminPenaltyJustification, setAdminPenaltyJustification] = useState('');
  const [adminPenaltySaving, setAdminPenaltySaving] = useState(false);

  // Linen split state (admin)
  const [linenFdmId, setLinenFdmId] = useState('');
  const [linenFdmPct, setLinenFdmPct] = useState(60);
  const [linenSaving, setLinenSaving] = useState(false);
  
  // Admin dialogs
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showAddTeamMemberDialog, setShowAddTeamMemberDialog] = useState(false); // Nouveau: ajouter un membre à l'équipe
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showAdminValidateDialog, setShowAdminValidateDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectPhotos, setRejectPhotos] = useState([]);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [controlComment, setControlComment] = useState('');
  const [controlRating, setControlRating] = useState(null);
  const [controlPhotos, setControlPhotos] = useState([]);
  const [controlPhotosPreviews, setControlPhotosPreviews] = useState([]);
  const controlPhotoInputRef = useRef(null);
  
  // Admin photo upload state
  const [adminPhotos, setAdminPhotos] = useState([]);
  const [adminPhotosPreviews, setAdminPhotosPreviews] = useState([]);
  const [adminPhotoUploading, setAdminPhotoUploading] = useState(false);
  const [adminArrivalTime, setAdminArrivalTime] = useState('');
  const [adminDepartureTime, setAdminDepartureTime] = useState('');
  const adminPhotoInputRef = useRef(null);
  
  // Reassign housekeeper state
  const [availableHousekeepers, setAvailableHousekeepers] = useState([]);
  const [selectedNewHousekeepers, setSelectedNewHousekeepers] = useState([]); // Changed to array for multi-select
  const [loadingHousekeepers, setLoadingHousekeepers] = useState(false);
  
  // Offline mode state
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingPhotosCount, setPendingPhotosCount] = useState(0);
  const [pendingPhotoPreviews, setPendingPhotoPreviews] = useState([]); // Previews des photos hors-ligne
  const [syncing, setSyncing] = useState(false);
  
  // Custom time dialog for allow_late_photos users
  const [showCustomTimeDialog, setShowCustomTimeDialog] = useState(false);
  const [customEndHour, setCustomEndHour] = useState(14);
  const [customEndMinute, setCustomEndMinute] = useState(0);
  
  // Ring intercom state
  const [unlockingRing, setUnlockingRing] = useState(false);
  
  // Modal pour voir toutes les photos
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  
  // Non réalisable (unrealizable) state
  const [showUnrealizableDialog, setShowUnrealizableDialog] = useState(false);
  
  // GPS Permission Modal state
  const [showGPSModal, setShowGPSModal] = useState(false);
  
  // Share gallery state
  const [shareLink, setShareLink] = useState(null);
  const [sharingGallery, setSharingGallery] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  const [unrealizableReason, setUnrealizableReason] = useState('');
  const [unrealizableDetails, setUnrealizableDetails] = useState('');
  const [unrealizablePhoto, setUnrealizablePhoto] = useState(null);
  const [unrealizablePhotoPreview, setUnrealizablePhotoPreview] = useState(null);
  const unrealizablePhotoRef = useRef(null);
  
  // Check if user has allow_late_photos permission
  const allowLatePhotos = user?.allow_late_photos || false;

  const isAdmin = user?.role === 'admin';
  const isTechnicianOrAdmin = user?.role === 'admin' || user?.role === 'technician';

  // Fetch task data
  const fetchTask = useCallback(async () => {
    try {
      const res = await tasksApi.get(id);
      
      // Vérifier si la réponse contient une erreur
      if (res.data?.detail || !res.data?.id) {
        console.error('Task error:', res.data?.detail || 'Données invalides');
        toast.error(res.data?.detail || 'Impossible de charger cette mission');
        setTask(null);
        setLoading(false);
        return;
      }
      
      const taskData = res.data || {};
      
      // S'assurer que les photos sont toujours un tableau valide
      const photos = Array.isArray(taskData.photos) ? taskData.photos.filter(p => p && typeof p === 'string') : [];
      
      setTask(taskData);
      setUploadedPhotos(photos);
      setChecklist(taskData.checklist || {});
      setDishSoapStatus(taskData.dish_soap_status || null);
      setDishSoapPhoto(taskData.dish_soap_photo || null);
      // Pré-remplir avec l'override manuel OU les valeurs auto-détectées via flag FDM
      setLinenFdmId(taskData.linen_fdm_id || taskData.effective_linen_fdm_id || '');
      setLinenFdmPct(
        taskData.linen_fdm_percentage != null
          ? taskData.linen_fdm_percentage
          : (taskData.effective_linen_fdm_percentage != null ? taskData.effective_linen_fdm_percentage : 60)
      );    } catch (error) {
      console.error('Task fetch error:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors du chargement';
      toast.error(errorMessage);
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Sync pending items
  const handleSync = useCallback(async () => {
    if (!online || syncing) return;
    
    setSyncing(true);
    try {
      const result = await syncPendingItems(token, (progress) => {
        console.log('Sync progress:', progress);
      });
      
      if (result.syncedPhotos > 0 || result.syncedActions > 0) {
        toast.success(`Synchronisation terminée`, {
          description: `${result.syncedPhotos} photo(s) et ${result.syncedActions} action(s) envoyées`
        });
        setPendingPhotosCount(0);
        setPendingPhotoPreviews([]); // Nettoyer les previews après sync
        fetchTask();
      }
      
      if (result.errors?.length > 0) {
        console.error('Sync errors:', result.errors);
        toast.warning(`${result.errors.length} erreur(s) lors de la synchronisation`, {
          description: 'Certaines photos seront réessayées plus tard'
        });
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  }, [online, syncing, token, fetchTask]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      toast.success('Connexion rétablie !');
      // Auto-sync when back online - use setTimeout to avoid immediate call
      setTimeout(() => handleSync(), 500);
    };
    const handleOffline = () => {
      setOnline(false);
      toast.warning('Mode hors-ligne activé', {
        description: 'Vos photos seront envoyées automatiquement au retour du réseau'
      });
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleSync]);

  // Check for pending items on load
  useEffect(() => {
    const checkPending = async () => {
      const pending = await getPendingPhotos(id);
      setPendingPhotosCount(pending.length);
      // Générer les previews des photos en attente
      const previews = pending.map(p => ({
        id: p.id,
        preview: p.base64, // base64 déjà stocké
        filename: p.filename
      }));
      setPendingPhotoPreviews(previews);
    };
    checkPending();
  }, [id]);

  // Load task on mount
  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await tasksApi.accept(id);
      toast.success('Mission acceptée !');
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStart = async () => {
    // Vérifier si le logement a des coordonnées GPS
    const propertyLat = task.property_latitude;
    const propertyLon = task.property_longitude;
    
    if (propertyLat && propertyLon) {
      // Le logement a des coordonnées GPS, ouvrir le modal de permission
      setShowGPSModal(true);
    } else {
      // Pas de coordonnées GPS sur le logement, démarrer directement
      await startTaskWithoutGPS();
    }
  };

  // Fonction appelée quand la position GPS est récupérée avec succès
  const handleGPSSuccess = async (userLat, userLon) => {
    setShowGPSModal(false);
    setActionLoading(true);
    
    try {
      const propertyLat = task.property_latitude;
      const propertyLon = task.property_longitude;
      
      // Calculer la distance
      const R = 6371000;
      const dLat = (propertyLat - userLat) * Math.PI / 180;
      const dLon = (propertyLon - userLon) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(userLat * Math.PI / 180) * Math.cos(propertyLat * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      
      // Validation côté frontend (20m)
      if (distance > 45) {
        toast.error(`📍 Vous êtes trop loin du logement (${Math.round(distance)}m). Vous devez être à moins de 45m pour commencer la prestation.`, {
          duration: 6000
        });
        setActionLoading(false);
        return;
      }
      
      // Appeler l'API pour démarrer la prestation
      await tasksApi.start(id, userLat, userLon);
      toast.success(`📍 Arrivée enregistrée ! (${Math.round(distance)}m du logement)`);
      await fetchTask();
      // Ouvre directement la modal produit vaisselle au début de la prestation
      // pour que la FDM le vérifie en arrivant (avant la serpillère)
      if (!dishSoapStatus || !dishSoapPhoto) {
        setShowDishSoapDialog(true);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du démarrage');
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour démarrer sans GPS (logement sans coordonnées)
  const startTaskWithoutGPS = async () => {
    setActionLoading(true);
    try {
      await tasksApi.start(id, null, null);
      toast.success("📍 Arrivée enregistrée ! Le chrono est lancé.");
      await fetchTask();
      // Ouvre directement la modal produit vaisselle au début (idem flow GPS)
      if (!dishSoapStatus || !dishSoapPhoto) {
        setShowDishSoapDialog(true);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndClock = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tasks/${id}/end-clock`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`⏱️ ${data.message}`);
        fetchTask();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors du pointage');
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour créer/copier le lien de partage de la galerie
  const handleShareGallery = async () => {
    setSharingGallery(true);
    try {
      // Vérifier si un lien existe déjà
      let response = await fetch(`${API_URL}/api/tasks/${id}/share-gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let data = await response.json();
      
      // Si pas de lien, en créer un
      if (!data.share_token) {
        response = await fetch(`${API_URL}/api/tasks/${id}/share-gallery`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        data = await response.json();
      }
      
      if (data.share_token) {
        const shareUrl = `${window.location.origin}/gallery/${data.share_token}`;
        setShareLink(shareUrl);
        
        // Copier dans le presse-papier
        await navigator.clipboard.writeText(shareUrl);
        setLinkCopied(true);
        toast.success('Lien copié dans le presse-papier !');
        
        setTimeout(() => setLinkCopied(false), 3000);
      }
    } catch (error) {
      toast.error('Erreur lors de la création du lien');
    } finally {
      setSharingGallery(false);
    }
  };

  // Formater le temps passé
  const formatTimeSpent = (minutes) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins.toString().padStart(2, '0')}`;
    }
    return `${mins} min`;
  };

  const handleComplete = async () => {
    // Check photos (including pending offline photos)
    const totalPhotos = uploadedPhotos.length + pendingPhotosCount;
    if (totalPhotos < 5) {
      toast.error(`Minimum 5 photos requises (${totalPhotos}/5)`);
      return;
    }

    // Vérification produit vaisselle obligatoire : ouvre la modal si pas fait
    if (!dishSoapStatus || !dishSoapPhoto) {
      setShowDishSoapDialog(true);
      return;
    }
    
    // Si online et photos en attente, essayer de sync d'abord
    if (pendingPhotosCount > 0 && online) {
      toast.info('Synchronisation des photos en cours...');
      await handleSync();
      // Continuer même si sync partielle
    }
    
    // Show keybox photo dialog first (mandatory step, unless FDM has skip_keybox_photo flag)
    if (user?.skip_keybox_photo) {
      // Skip la photo boîte à clés (option admin), aller directement à la validation finale
      setShowPostValidationDialog(true);
      return;
    }
    setKeyboxPhoto(null);
    setShowKeyboxPhotoDialog(true);
  };

  // Handle keybox photo capture
  const handleKeyboxPhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setKeyboxPhotoUploading(true);
    
    try {
      // Get GPS coordinates if available
      let gpsData = null;
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          });
        });
        gpsData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
      } catch (gpsErr) {
        console.log('GPS not available for keybox photo:', gpsErr);
      }
      
      // ⚠️ Convert to JPEG (fixes HEIC/HEIF iPhone photos which the backend can't process)
      // compressImageFast rescales to ≤1200px, adds a timestamp watermark, and outputs JPEG.
      let uploadFile;
      try {
        uploadFile = await compressImageFast(file);
      } catch (compressErr) {
        console.warn('Keybox photo compression failed, sending raw file:', compressErr);
        uploadFile = file;
      }

      // Create FormData with GPS info
      const formData = new FormData();
      formData.append('file', uploadFile, uploadFile.name || 'keybox.jpg');
      formData.append('is_keybox_photo', 'true');
      if (gpsData) {
        formData.append('photo_latitude', gpsData.latitude.toString());
        formData.append('photo_longitude', gpsData.longitude.toString());
      }
      
      // Upload the photo
      const response = await fetch(`${API_URL}/api/tasks/${id}/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (_e) {
          // Response is not JSON (HTML error page, 502 gateway, etc.)
          const text = await response.text().catch(() => '');
          console.error('Keybox upload — non-JSON error response:', response.status, text?.slice(0, 300));
          throw new Error(`Erreur serveur (${response.status}). Réessayez dans quelques secondes.`);
        }
        console.error('Keybox upload — server error:', response.status, errorData);
        throw new Error(extractErrorMessage(errorData, `Erreur ${response.status} lors de l'upload`));
      }
      
      const result = await response.json();
      setKeyboxPhoto(result.path || result.photo_url || result.storage_path);
      toast.success('Photo de la boîte à clés enregistrée');
      
      // Refresh photos list
      fetchTask();
      
    } catch (error) {
      console.error('Keybox photo upload error:', error);
      toast.error(extractErrorMessage(error, "Erreur lors de l'upload de la photo"));
    } finally {
      setKeyboxPhotoUploading(false);
      // Reset input
      if (keyboxPhotoInputRef.current) {
        keyboxPhotoInputRef.current.value = '';
      }
    }
  };

  // Continue to post-validation dialog after keybox photo
  const handleKeyboxPhotoConfirmed = () => {
    if (!keyboxPhoto) {
      toast.error('Veuillez d\'abord prendre une photo de la boîte à clés');
      return;
    }
    setShowKeyboxPhotoDialog(false);
    setShowPostValidationDialog(true);
  };

  const handleFinalComplete = async (hasIssue = false) => {
    if (hasIssue) {
      // User wants to report an issue
      setShowPostValidationDialog(false);
      setShowProblemDialog(true);
      return;
    }
    
    setActionLoading(true);
    
    // Mode HORS-LIGNE : sauvegarder l'action en local
    if (!online) {
      try {
        await savePendingAction(id, 'complete', { completedAt: new Date().toISOString() });
        toast.success('Prestation validée !', {
          description: '📱 Sera synchronisée quand le réseau reviendra',
          duration: 5000
        });
        setShowPostValidationDialog(false);
        // Mettre à jour l'interface localement
        setTask(prev => ({ ...prev, status: 'termine_local', completed_at: new Date().toISOString() }));
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde locale');
      } finally {
        setActionLoading(false);
      }
      return;
    }
    
    // Mode ONLINE : compléter normalement
    try {
      await tasksApi.complete(id);
      toast.success('Prestation validée ! Merci pour votre travail.');
      setShowPostValidationDialog(false);
      fetchTask();
    } catch (error) {
      // === Facturation au temps réel : mission terminée en avance ===
      // Le backend renvoie 409 avec les détails pour proposer un choix à la FDM
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === 'TIME_BILLING_EARLY_COMPLETION') {
        setEarlyCompletionInfo(detail);
        setShowEarlyCompletionDialog(true);
        setActionLoading(false);
        return;
      }
      // Si erreur réseau, sauvegarder en local
      if (!navigator.onLine || error.message?.includes('Network')) {
        await savePendingAction(id, 'complete', { completedAt: new Date().toISOString() });
        toast.success('Prestation validée !', {
          description: '📱 Sera synchronisée quand le réseau reviendra',
          duration: 5000
        });
        setShowPostValidationDialog(false);
        setTask(prev => ({ ...prev, status: 'termine_local', completed_at: new Date().toISOString() }));
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Erreur'));
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Handler pour la validation "J'ai vraiment fini" (accepte la facturation au temps réel)
  const handleConfirmEarlyCompletion = async () => {
    setActionLoading(true);
    try {
      await tasksApi.complete(id, { confirmEarly: true });
      toast.success('Prestation validée', {
        description: earlyCompletionInfo?.adjusted_price != null
          ? `Facturé ${earlyCompletionInfo.adjusted_price}€ au temps réel`
          : undefined,
      });
      setShowEarlyCompletionDialog(false);
      setShowPostValidationDialog(false);
      setEarlyCompletionInfo(null);
      fetchTask();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Erreur'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleChecklistChange = async (itemId, checked) => {
    const newChecklist = { ...checklist, [itemId]: checked };
    setChecklist(newChecklist);
    
    // Auto-save checklist
    setChecklistSaving(true);
    try {
      await tasksApi.updateChecklist(id, { checklist: newChecklist });
    } catch (error) {
      console.error('Checklist save error:', error);
      // Revert on error
      setChecklist(checklist);
    } finally {
      setChecklistSaving(false);
    }
  };

  // Admin: save linen split (FDM responsable du linge + %)
  const handleSaveLinenSplit = async () => {
    setLinenSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/${id}/linen-split`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          linen_fdm_id: linenFdmId || null,
          linen_fdm_percentage: linenFdmId ? Number(linenFdmPct) : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Échec');
      }
      toast.success('Répartition linge enregistrée');
      await fetchTask();
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally {
      setLinenSaving(false);
    }
  };

  // Admin: apply or cancel dish soap penalty with justification
  const handleAdminPenalty = async (apply, justification) => {
    setAdminPenaltySaving(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/${id}/dish-soap-admin-penalty`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ apply, justification }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Échec de l'opération");
      }
      toast.success(apply ? "Pénalité appliquée" : "Pénalité annulée");
      setShowAdminPenaltyDialog(false);
      await fetchTask();
    } catch (err) {
      console.error('Admin penalty error:', err);
      toast.error(err.message || "Erreur");
    } finally {
      setAdminPenaltySaving(false);
    }
  };

  // Dish soap photo upload (mandatory before completing)
  const handleDishSoapPhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!dishSoapStatus) {
      toast.error("Choisissez d'abord 'Oui, présent' ou 'Non, remplacé'");
      return;
    }

    setDishSoapUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('status', dishSoapStatus);

      const res = await fetch(`${API_URL}/api/tasks/${id}/dish-soap`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Échec de l'upload");
      }

      const data = await res.json();
      setDishSoapPhoto(data.photo);
      setTask((prev) => prev ? { ...prev, dish_soap_status: data.status, dish_soap_photo: data.photo } : prev);
      toast.success("🧴 Produit vaisselle enregistré");
    } catch (err) {
      console.error('Dish soap upload error:', err);
      // Long-duration error toast so the FDM has time to read the AI rejection message
      toast.error(err.message || "Erreur lors de l'envoi", { duration: 8000 });
    } finally {
      setDishSoapUploading(false);
      if (dishSoapInputRef.current) dishSoapInputRef.current.value = '';
    }
  };

  // Fonction pour obtenir les coordonnées GPS actuelles
  const getCurrentGpsPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Géolocalisation non supportée'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          let message = 'Erreur de géolocalisation';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Veuillez autoriser la géolocalisation dans les paramètres de votre navigateur';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Position non disponible. Vérifiez que le GPS est activé';
              break;
            case error.TIMEOUT:
              message = 'La demande de géolocalisation a expiré';
              break;
          }
          reject(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000 // Cache position for 30 seconds
        }
      );
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Réinitialiser l'input immédiatement pour permettre de reprendre des photos
    if (photoInputRef.current) photoInputRef.current.value = '';
    
    setIsUploading(true);
    setUploadProgress(0);
    setGpsError(null);
    
    // Obtenir les coordonnées GPS actuelles (si l'utilisateur a l'option require_gps_photos)
    let gpsCoords = null;
    if (user?.require_gps_photos) {
      try {
        gpsCoords = await getCurrentGpsPosition();
        setCurrentGpsCoords(gpsCoords);
      } catch (gpsErr) {
        setGpsError(gpsErr.message);
        toast.error(`📍 ${gpsErr.message}`);
        setIsUploading(false);
        return; // Stop if GPS is required but not available
      }
    }
    
    const totalFiles = files.length;
    let processedCount = 0;
    let suspectPhotos = 0;

    for (const file of files) {
      try {
        // 1. PREVIEW IMMÉDIAT - afficher la photo avant toute compression
        const quickPreview = URL.createObjectURL(file);
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Ajouter le preview immédiatement (même hors-ligne)
        setPendingPhotoPreviews(prev => [...prev, {
          id: tempId,
          preview: quickPreview,
          filename: file.name,
          status: 'processing'
        }]);
        setPendingPhotosCount(prev => prev + 1);
        
        // 2. VÉRIFICATION ANTI-FRAUDE pour les photos depuis la galerie (tâche terminée)
        let verificationResult = null;
        if (task.status === 'termine') {
          try {
            verificationResult = await analyzePhoto(file, task.date);
            
            // Photos très suspectes (score < 30 = très probablement IA)
            // On les upload silencieusement - l'admin verra dans "Photos suspectes"
            if (verificationResult.trustScore < 30) {
              suspectPhotos++;
              verificationResult.highlySupect = true;
            } else if (verificationResult.verdict === 'suspect') {
              suspectPhotos++;
            }
            // Pas d'avertissement à la FDM - l'admin vérifiera
          } catch (err) {
            console.log('Vérification photo ignorée:', err);
          }
        }
        
        // 3. COMPRESSION en arrière-plan (rapide, max 1200px)
        // Si tâche terminée, utiliser la date/heure de validation pour le watermark
        const watermarkTimestamp = (task.status === 'termine' && task.completed_at) 
          ? new Date(task.completed_at) 
          : null;
        const compressedFile = await compressImageFast(file, watermarkTimestamp);
        
        // 4. SAUVEGARDE - soit upload online, soit stockage local
        if (online) {
          try {
            const res = await tasksApi.uploadPhoto(id, compressedFile, verificationResult, gpsCoords);
            // Upload réussi - remplacer le preview par la vraie photo
            setUploadedPhotos(prev => [...prev, res.data.path]);
            setPendingPhotoPreviews(prev => prev.filter(p => p.id !== tempId));
            setPendingPhotosCount(prev => Math.max(0, prev - 1));
          } catch (uploadError) {
            // Vérifier si c'est une erreur GPS (400) — extract message safely
            const errMsg = extractErrorMessage(uploadError, '');
            if (uploadError.response?.status === 400 && errMsg.includes('📍')) {
              toast.error(errMsg);
              setPendingPhotoPreviews(prev => prev.filter(p => p.id !== tempId));
              setPendingPhotosCount(prev => Math.max(0, prev - 1));
            } else {
              // Upload échoué - sauvegarder en local
              console.log('Upload failed, saving offline:', errMsg || uploadError.message);
              await savePhotoLocally(tempId, compressedFile, file.name);
            }
          }
        } else {
          // Hors-ligne - sauvegarder directement en local
          await savePhotoLocally(tempId, compressedFile, file.name);
        }
        
        processedCount++;
        setUploadProgress(Math.round((processedCount / totalFiles) * 100));
        
      } catch (error) {
        console.error('Error processing photo:', error);
        processedCount++;
      }
    }
    
    // Message de confirmation
    const offlineCount = pendingPhotoPreviews.filter(p => p.status === 'saved').length;
    if (offlineCount > 0) {
      toast.success(`${totalFiles} photo(s) sauvegardée(s)`, {
        description: online ? '✅ Envoi en cours...' : '📱 Seront envoyées avec le réseau',
        duration: 3000
      });
    } else if (online) {
      toast.success(`${totalFiles} photo(s) envoyée(s)`);
      fetchTask();
    }
    
    setIsUploading(false);
    setUploadProgress(0);
  };

  // Sauvegarde locale rapide
  const savePhotoLocally = async (tempId, compressedFile, filename) => {
    try {
      const saved = await savePendingPhoto(id, compressedFile, filename);
      if (saved) {
        // Mettre à jour le status du preview
        setPendingPhotoPreviews(prev => prev.map(p => 
          p.id === tempId ? { ...p, status: 'saved' } : p
        ));
      }
    } catch (error) {
      console.error('Error saving locally:', error);
    }
  };

  // Compression rapide (max 1200px, qualité 0.7)
  // customTimestamp: Date optionnelle pour le watermark (utilisé pour les photos différées)
  const compressImageFast = (file, customTimestamp = null) => {
    return new Promise((resolve) => {
      // Si le fichier est petit (< 500KB), on le compresse quand même pour le watermark
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = document.createElement('img');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 1200; // Plus petit = plus rapide
          let width = img.width;
          let height = img.height;
          
          if (width > height && width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          } else if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Watermark avec date/heure (utiliser customTimestamp si fourni = heure de validation)
          const timestampDate = customTimestamp || new Date();
          const timestamp = timestampDate.toLocaleDateString('fr-FR') + ' ' + timestampDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
          
          // Style du watermark
          ctx.font = 'bold 18px Arial';
          ctx.fillStyle = 'rgba(255,255,255,0.95)';
          ctx.strokeStyle = 'rgba(0,0,0,0.7)';
          ctx.lineWidth = 3;
          
          // Position en bas à droite
          const textWidth = ctx.measureText(timestamp).width;
          const x = canvas.width - textWidth - 15;
          const y = canvas.height - 15;
          
          ctx.strokeText(timestamp, x, y);
          ctx.fillText(timestamp, x, y);
          
          // Si c'est une photo différée, ajouter un indicateur
          if (customTimestamp) {
            ctx.font = 'bold 12px Arial';
            const label = '✓ Validé';
            ctx.strokeText(label, x, y - 22);
            ctx.fillText(label, x, y - 22);
          }
          
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              resolve(file); // Fallback si toBlob échoue
            }
          }, 'image/jpeg', 0.7);
        };
        img.onerror = () => resolve(file); // Fallback
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file); // Fallback
      reader.readAsDataURL(file);
    });
  };

  const handleProblemPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        const compressedFile = await compressImage(file);
        const res = await tasksApi.uploadProblemPhoto(id, compressedFile);
        setProblemPhotos(prev => [...prev, res.data.path]);
      }
      toast.success(`${files.length} photo(s) ajoutée(s) au problème`);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      if (problemPhotoInputRef.current) problemPhotoInputRef.current.value = '';
    }
  };

  const addTimestampToImage = (canvas, ctx) => {
    const now = new Date();
    const timestamp = format(now, "dd/MM/yyyy HH:mm:ss", { locale: fr });
    
    // Configure text style
    const fontSize = Math.max(20, Math.floor(canvas.width / 40));
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    
    // Measure text
    const textMetrics = ctx.measureText(timestamp);
    const padding = 10;
    const boxWidth = textMetrics.width + padding * 2;
    const boxHeight = fontSize + padding * 2;
    
    // Draw semi-transparent background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(
      canvas.width - boxWidth - 10,
      canvas.height - boxHeight - 10,
      boxWidth,
      boxHeight
    );
    
    // Draw text
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      timestamp,
      canvas.width - 20,
      canvas.height - 20
    );
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = document.createElement('img');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 1920;
          let width = img.width;
          let height = img.height;
          if (width > height && width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          } else if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Add timestamp watermark
          addTimestampToImage(canvas, ctx);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.85);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleProblem = async () => {
    if (!problemText.trim()) {
      toast.error('Veuillez décrire le problème');
      return;
    }
    setActionLoading(true);
    try {
      await tasksApi.reportProblem(id, { text: problemText });
      toast.success('Problème signalé à l\'administrateur');
      setShowProblemDialog(false);
      setProblemText('');
      setProblemPhotos([]);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle unrealizable photo selection
  const handleUnrealizablePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setUnrealizablePhotoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    // Compress and store file
    const compressedFile = await compressImage(file);
    setUnrealizablePhoto(compressedFile);
  };

  // Handle unrealizable task report
  const handleUnrealizable = async () => {
    if (!unrealizableReason) {
      toast.error('Veuillez sélectionner une raison');
      return;
    }
    
    if (unrealizableReason === 'other' && !unrealizableDetails.trim()) {
      toast.error('Veuillez décrire le problème');
      return;
    }
    
    setActionLoading(true);
    try {
      let photoPath = null;
      
      // Upload photo if provided
      if (unrealizablePhoto) {
        const formData = new FormData();
        formData.append('file', unrealizablePhoto);
        const uploadRes = await fetch(`${API_URL}/api/tasks/${id}/photos`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          photoPath = uploadData.path;
        }
      }
      
      // Report task as unrealizable
      const response = await fetch(`${API_URL}/api/tasks/${id}/unrealizable`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: unrealizableReason,
          reason_details: unrealizableDetails || null,
          photo: photoPath
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors du signalement');
      }
      
      toast.success('Prestation signalée comme non réalisable');
      setShowUnrealizableDialog(false);
      setUnrealizableReason('');
      setUnrealizableDetails('');
      setUnrealizablePhoto(null);
      setUnrealizablePhotoPreview(null);
      fetchTask();
    } catch (error) {
      toast.error(error.message || 'Erreur lors du signalement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProblemAndComplete = async () => {
    if (!problemText.trim()) {
      toast.error('Veuillez décrire le problème');
      return;
    }
    setActionLoading(true);
    try {
      // First report the problem
      await tasksApi.reportProblem(id, { text: problemText });
      // Then complete the task
      await tasksApi.complete(id);
      toast.success('Prestation validée avec signalement envoyé');
      setShowProblemDialog(false);
      setProblemText('');
      setProblemPhotos([]);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  // Admin actions
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Veuillez indiquer la raison du refus');
      return;
    }
    setActionLoading(true);
    try {
      await tasksApi.reject(id, { reason: rejectReason, photos: rejectPhotos });
      toast.success('Prestation refusée');
      setShowRejectDialog(false);
      setRejectReason('');
      setRejectPhotos([]);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReset = async () => {
    setActionLoading(true);
    try {
      await tasksApi.reset(id);
      toast.success('Tâche réinitialisée, prête à être réassignée');
      setShowResetDialog(false);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  // Open reset dialog and fetch available housekeepers
  const openResetDialog = async () => {
    setShowResetDialog(true);
    setLoadingHousekeepers(true);
    
    try {
      // Fetch ALL assignable housekeepers (don't filter out current ones)
      const res = await fetch(`${API_URL}/api/users?assignable=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const housekeepers = await res.json();
        setAvailableHousekeepers(housekeepers);
        
        // Pré-sélectionner les FDM actuellement assignées
        const currentIds = task?.housekeeper_ids || (task?.housekeeper_id ? [task.housekeeper_id] : []);
        const currentHousekeepers = housekeepers.filter(h => currentIds.includes(h.id));
        setSelectedNewHousekeepers(currentHousekeepers);
      }
    } catch (error) {
      console.error('Error fetching housekeepers:', error);
      toast.error('Erreur lors du chargement des FDM');
    } finally {
      setLoadingHousekeepers(false);
    }
  };

  // Reassign to new housekeeper(s) - smart: only reset if team changes
  const handleReassign = async () => {
    if (selectedNewHousekeepers.length === 0) {
      toast.error('Veuillez sélectionner au moins une femme de ménage');
      return;
    }
    
    if (selectedNewHousekeepers.length > 2) {
      toast.error('Maximum 2 femmes de ménage par prestation');
      return;
    }
    
    const newIds = selectedNewHousekeepers.map(h => h.id).sort();
    const currentIds = (task?.housekeeper_ids || (task?.housekeeper_id ? [task.housekeeper_id] : [])).sort();
    
    // Check if the team actually changed
    const teamChanged = newIds.length !== currentIds.length || 
                        !newIds.every((id, idx) => currentIds[idx] === id);
    
    if (!teamChanged) {
      toast.info('L\'équipe n\'a pas changé');
      setShowResetDialog(false);
      return;
    }
    
    setActionLoading(true);
    try {
      // Check if we're ADDING someone (current team is subset of new team)
      const isAdding = currentIds.every(id => newIds.includes(id)) && newIds.length > currentIds.length;
      
      if (isAdding) {
        // Just add without reset
        await fetch(`${API_URL}/api/tasks/${id}/assign`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ housekeeper_ids: newIds })
        });
      } else {
        // Team completely changed - reset first then assign
        await tasksApi.reset(id);
        await fetch(`${API_URL}/api/tasks/${id}/assign`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ housekeeper_ids: newIds })
        });
      }
      
      const names = selectedNewHousekeepers.map(h => h.name).join(' & ');
      toast.success(`Équipe mise à jour : ${names}`);
      setShowResetDialog(false);
      setSelectedNewHousekeepers([]);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Toggle housekeeper selection (for multi-select)
  const toggleHousekeeperSelection = (hk) => {
    setSelectedNewHousekeepers(prev => {
      const isSelected = prev.some(h => h.id === hk.id);
      if (isSelected) {
        return prev.filter(h => h.id !== hk.id);
      } else {
        if (prev.length >= 2) {
          toast.error('Maximum 2 femmes de ménage');
          return prev;
        }
        return [...prev, hk];
      }
    });
  };

  // Ouvrir le dialog pour ajouter un membre à l'équipe
  const openAddTeamMemberDialog = async () => {
    setShowAddTeamMemberDialog(true);
    setSelectedNewHousekeepers([]);
    setLoadingHousekeepers(true);
    try {
      const res = await fetch(`${API_URL}/api/users?assignable=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const housekeepers = await res.json();
        // Filter out currently assigned housekeepers
        const currentIds = task?.housekeeper_ids || (task?.housekeeper_id ? [task.housekeeper_id] : []);
        const available = housekeepers.filter(h => !currentIds.includes(h.id));
        setAvailableHousekeepers(available);
      }
    } catch (error) {
      console.error('Error fetching housekeepers:', error);
      toast.error('Erreur lors du chargement des FDM');
    } finally {
      setLoadingHousekeepers(false);
    }
  };

  // Ajouter un membre à l'équipe existante (sans reset)
  const handleAddTeamMember = async () => {
    if (selectedNewHousekeepers.length === 0) {
      toast.error('Veuillez sélectionner une femme de ménage à ajouter');
      return;
    }
    
    // Get current housekeeper IDs
    const currentIds = task?.housekeeper_ids || (task?.housekeeper_id ? [task.housekeeper_id] : []);
    const newIds = [...currentIds, ...selectedNewHousekeepers.map(h => h.id)];
    
    if (newIds.length > 2) {
      toast.error('Maximum 2 femmes de ménage par prestation');
      return;
    }
    
    setActionLoading(true);
    try {
      // Directly assign with the combined list (no reset)
      await fetch(`${API_URL}/api/tasks/${id}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ housekeeper_ids: newIds })
      });
      
      const names = selectedNewHousekeepers.map(h => h.name).join(' & ');
      toast.success(`${names} ajouté(e) à l'équipe`);
      setShowAddTeamMemberDialog(false);
      setSelectedNewHousekeepers([]);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate) {
      toast.error('Veuillez sélectionner une date');
      return;
    }
    setActionLoading(true);
    try {
      await tasksApi.reschedule(id, { new_date: rescheduleDate, reason: rescheduleReason || undefined });
      toast.success('Date de prestation modifiée');
      setShowRescheduleDialog(false);
      setRescheduleDate('');
      setRescheduleReason('');
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleControlComment = async () => {
    if (!controlComment.trim()) {
      toast.error('Veuillez saisir un commentaire');
      return;
    }
    setActionLoading(true);
    try {
      // Upload photos first
      const uploadedPhotoPaths = [];
      for (const file of controlPhotos) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await tasksApi.uploadControlPhoto(id, formData);
        uploadedPhotoPaths.push(res.data.path);
      }
      
      // Send comment with photos and rating
      await tasksApi.addControlComment(id, { 
        comment: controlComment,
        photos: uploadedPhotoPaths,
        rating: controlRating
      });
      toast.success('Évaluation envoyée ! La femme de ménage sera notifiée par email.');
      setShowCommentDialog(false);
      setControlComment('');
      setControlRating(null);
      setControlPhotos([]);
      setControlPhotosPreviews([]);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleControlPhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + controlPhotos.length > 10) {
      toast.error('Maximum 10 photos');
      return;
    }
    
    setControlPhotos(prev => [...prev, ...files]);
    
    // Generate previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setControlPhotosPreviews(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeControlPhoto = (index) => {
    setControlPhotos(prev => prev.filter((_, i) => i !== index));
    setControlPhotosPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Admin photo selection for validation
  const handleAdminPhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + adminPhotos.length > 20) {
      toast.error('Maximum 20 photos');
      return;
    }
    
    setAdminPhotos(prev => [...prev, ...files]);
    
    // Generate previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAdminPhotosPreviews(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAdminPhoto = (index) => {
    setAdminPhotos(prev => prev.filter((_, i) => i !== index));
    setAdminPhotosPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Admin force validation
  const handleAdminValidate = async () => {
    setActionLoading(true);
    try {
      // Upload admin photos if any
      if (adminPhotos.length > 0) {
        setAdminPhotoUploading(true);
        for (const file of adminPhotos) {
          const formData = new FormData();
          formData.append('file', file);
          await fetch(`${API_URL}/api/tasks/${id}/photos`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
        }
        setAdminPhotoUploading(false);
      }
      
      // Force complete the task (avec heures arrivée/départ manuelles si renseignées)
      await fetch(`${API_URL}/api/tasks/${id}/admin-validate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          arrival_time: adminArrivalTime || null,
          departure_time: adminDepartureTime || null
        })
      });
      
      toast.success('Prestation validée par l\'administrateur');
      setShowAdminValidateDialog(false);
      setAdminPhotos([]);
      setAdminPhotosPreviews([]);
      setAdminArrivalTime('');
      setAdminDepartureTime('');
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setActionLoading(false);
      setAdminPhotoUploading(false);
    }
  };

  // Download PDF report with certificate
  const downloadPdfReport = async () => {
    if (!task || !task.photos || task.photos.length === 0) {
      toast.error('Aucune photo disponible pour générer le rapport');
      return;
    }
    
    toast.info('Génération du rapport PDF...');
    try {
      const response = await fetch(`${API_URL}/api/tasks/${id}/report-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Erreur lors de la génération du PDF';
        try {
          const errorData = await response.clone().json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rapport_${task.property_name}_${task.date}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Rapport PDF téléchargé avec succès');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error.message || 'Erreur lors du téléchargement du PDF');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      en_attente: 'bg-orange-100 text-orange-700',
      affectee: 'bg-blue-100 text-blue-700',
      acceptee: 'bg-sky-100 text-sky-700',
      en_cours: 'bg-purple-100 text-purple-700',
      termine: 'bg-emerald-100 text-emerald-700',
      termine_local: 'bg-amber-100 text-amber-700',
      probleme: 'bg-red-100 text-red-700',
      non_realisable: 'bg-red-200 text-red-800 font-semibold',
      rejete: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      en_attente: 'En attente',
      affectee: 'À accepter',
      acceptee: 'Acceptée',
      en_cours: 'En cours',
      termine: 'Terminé',
      termine_local: '✓ Validé (sync...)',
      probleme: 'Problème',
      non_realisable: '🚫 Non réalisable',
      rejete: 'Refusé'
    };
    return (
      <Badge className={`${styles[status] || 'bg-gray-100 text-gray-700'} border-0 text-sm px-3 py-1`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getStepIndicator = () => {
    const steps = ['affectee', 'acceptee', 'en_cours', 'termine'];
    const currentIndex = steps.indexOf(task.status);
    if (['probleme', 'rejete', 'non_realisable'].includes(task.status)) return null;

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
        {steps.map((step, idx) => (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                idx <= currentIndex ? 'bg-[#003366] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {idx <= currentIndex ? <CheckCircle2 className="h-4 w-4" /> : idx + 1}
              </div>
              <span className={`text-xs mt-1 ${idx <= currentIndex ? 'text-[#003366] font-medium' : 'text-gray-400'}`}>
                {idx === 0 ? 'Affectée' : idx === 1 ? 'Acceptée' : idx === 2 ? 'En cours' : 'Terminé'}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${idx < currentIndex ? 'bg-[#003366]' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const openMaps = () => {
    const address = encodeURIComponent(task.property_address || task.property_name);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  const openWaze = () => {
    const address = encodeURIComponent(task.property_address || task.property_name);
    window.open(`https://waze.com/ul?q=${address}&navigate=yes`, '_blank');
  };

  const openGoogleMapsDirections = () => {
    const address = encodeURIComponent(task.property_address || task.property_name);
    // On mobile, this will open the Google Maps app if installed
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=driving`, '_blank');
  };

  // Ring intercom unlock
  const unlockRingIntercom = async () => {
    if (!task?.ring_device_id) return;
    
    setUnlockingRing(true);
    try {
      const res = await fetch(`${API_URL}/api/ring/unlock/${task.ring_device_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Porte déverrouillée !');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors du déverrouillage');
      }
    } catch (error) {
      console.error('Ring unlock error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setUnlockingRing(false);
    }
  };

  // Calculate checklist completion
  const checklistCompletedCount = CHECKLIST_ITEMS.filter(item => checklist[item.id]).length;
  const checklistComplete = checklistCompletedCount === CHECKLIST_ITEMS.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#003366]" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col gap-4 p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Mission non disponible</h2>
          <p className="text-gray-600 mb-4">Cette mission n'existe pas ou vous n'avez pas accès à cette prestation.</p>
          <Button onClick={() => navigate(-1)} className="bg-[#003366]">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  // Protection contre les données corrompues
  if (!task.id || !task.property_name) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col gap-4 p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Erreur de données</h2>
          <p className="text-gray-600 mb-4">Les données de cette mission sont incomplètes.</p>
          <Button onClick={() => navigate(-1)} className="bg-[#003366]">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-64">
      {/* Header */}
      <div className="bg-[#003366] text-white p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold truncate">{task.property_name}</h1>
            <button onClick={openMaps} className="text-white/70 text-sm truncate flex items-center gap-1 hover:text-white">
              <MapPin className="h-3 w-3" />
              {task.property_address}
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
          {getStatusBadge(task.status)}
          
          {/* Admin/Tech Menu */}
          {isTechnicianOrAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" data-testid="admin-menu-btn">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setShowCommentDialog(true)} data-testid="comment-menu-item">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Ajouter un commentaire
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => {
                  try {
                    setRescheduleDate(format(addDays(new Date(task.date || new Date()), 1), 'yyyy-MM-dd'));
                  } catch(e) {
                    setRescheduleDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
                  }
                  setShowRescheduleDialog(true);
                }} data-testid="reschedule-menu-item">
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Changer la date
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openResetDialog()} data-testid="reset-menu-item">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Réinitialiser (changer équipe)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowRejectDialog(true)} className="text-red-600 focus:text-red-600" data-testid="reject-menu-item">
                  <XCircle className="h-4 w-4 mr-2" />
                  Refuser la prestation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Offline Banner */}
      {(!online || pendingPhotosCount > 0) && (
        <div className={`px-4 py-2 flex items-center justify-between ${online ? 'bg-blue-100' : 'bg-orange-100'}`}>
          <div className="flex items-center gap-2">
            {online ? (
              <>
                <CloudUpload className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  {pendingPhotosCount} photo(s) en attente de synchronisation
                </span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-orange-600" />
                <span className="text-sm text-orange-700">
                  Mode hors-ligne {pendingPhotosCount > 0 && `• ${pendingPhotosCount} photo(s) sauvegardées`}
                </span>
              </>
            )}
          </div>
          {online && pendingPhotosCount > 0 && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-blue-600 hover:bg-blue-200"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Synchroniser'}
            </Button>
          )}
        </div>
      )}

      {/* Step Indicator */}
      {isHousekeeper && getStepIndicator()}

      <div className="p-4 space-y-4">
        {/* Task Info Card */}
        <Card className="border-0 shadow-sm" data-testid="task-info-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium">
                  {task.date ? format(new Date(task.date), "EEEE d MMMM yyyy", { locale: fr }) : 'Date non définie'}
                </p>
                {task.rescheduled_from && (
                  <p className="text-xs text-orange-600">
                    Reporté depuis le {(() => { try { return format(new Date(task.rescheduled_from), "d MMMM", { locale: fr }); } catch(e) { return 'date inconnue'; } })()}
                  </p>
                )}
              </div>
            </div>

            {task.departure_time && (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Heure de départ voyageurs</p>
                  <p className="font-medium">{task.departure_time}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <Euro className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Rémunération</p>
                <p className="font-bold text-lg text-[#003366]">{task.price || 0}€</p>
                {task.time_billing_adjustment && (
                  <div className="mt-1 inline-flex items-center gap-1 text-xs bg-purple-50 border border-purple-200 rounded-md px-2 py-0.5 text-purple-700" data-testid="time-billing-badge">
                    <span>⏱️</span>
                    <span>
                      Temps réduit : {task.time_billing_adjustment.actual_minutes}min au lieu de {task.time_billing_adjustment.expected_minutes}min → {task.time_billing_adjustment.adjusted_price}€ au lieu de {task.time_billing_adjustment.original_price}€
                    </span>
                  </div>
                )}
              </div>
            </div>

            {task.platform && (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Home className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Plateforme</p>
                  <p className="font-medium">{task.platform}</p>
                </div>
              </div>
            )}

            {/* Assigned housekeepers - show for admins */}
            {task.housekeeper_names && task.housekeeper_names.length > 0 && !isHousekeeper && (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Users className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Assigné à</p>
                  <p className="font-medium text-[#003366]">{task.housekeeper_names.join(' & ')}</p>
                </div>
              </div>
            )}

            {task.notes && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-500 mb-1">Notes</p>
                <p className="text-gray-700">{task.notes}</p>
              </div>
            )}

            {/* Temporary Note / Special Instructions for this task */}
            {task.temporary_note && (
              <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <span className="font-bold text-orange-800">Instructions spéciales</span>
                </div>
                <p className="text-orange-900 font-medium text-lg">{task.temporary_note}</p>
              </div>
            )}

            {/* Access Codes Section - For all users */}
            {(task.property_keybox_code || task.property_building_code || task.property_keybox_address || task.property_access_instructions) && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                <p className="font-semibold text-blue-800 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Codes d'accès
                </p>
                
                {/* Adresse boîte à clés si différente */}
                {task.property_keybox_address && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-600 font-medium mb-1">Adresse boîte à clés (différente du logement)</p>
                    <p className="text-sm font-semibold text-amber-800">{task.property_keybox_address}</p>
                    {task.property_keybox_position && (
                      <p className="text-xs text-amber-600 mt-1">N° boîte sur façade : {task.property_keybox_position}</p>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  {task.property_keybox_code && (
                    <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <p className="text-xs text-gray-500">Boîte à clés</p>
                      <p className="text-2xl font-mono font-bold text-blue-700">{task.property_keybox_code}</p>
                    </div>
                  )}
                  {task.property_building_code && (
                    <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <p className="text-xs text-gray-500">Code immeuble</p>
                      <p className="text-2xl font-mono font-bold text-blue-700">{task.property_building_code}</p>
                    </div>
                  )}
                </div>
                
                {/* Instructions supplémentaires */}
                {task.property_access_instructions && (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Instructions</p>
                    <p className="text-sm text-gray-700">{task.property_access_instructions}</p>
                  </div>
                )}
              </div>
            )}

            {/* Navigation GPS Section */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
              <p className="font-semibold text-emerald-800 flex items-center gap-2">
                <Navigation className="h-4 w-4" />
                Navigation GPS
              </p>
              <p className="text-sm text-emerald-600 mb-2">
                📍 {task.property_address || task.property_name}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={openGoogleMapsDirections}
                  className="bg-white border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100 flex items-center justify-center gap-2 h-12 rounded-xl shadow-sm"
                  data-testid="google-maps-btn"
                >
                  <Map className="h-5 w-5" />
                  <span className="font-semibold">Google Maps</span>
                </Button>
                <Button 
                  onClick={openWaze}
                  className="bg-white border-2 border-cyan-300 text-cyan-700 hover:bg-cyan-100 flex items-center justify-center gap-2 h-12 rounded-xl shadow-sm"
                  data-testid="waze-btn"
                >
                  <Navigation className="h-5 w-5" />
                  <span className="font-semibold">Waze</span>
                </Button>
              </div>
              
              {/* Ring Intercom Button */}
              {task.ring_device_id && (
                <Button 
                  onClick={unlockRingIntercom}
                  disabled={unlockingRing}
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white flex items-center justify-center gap-3 h-14 rounded-xl shadow-lg"
                  data-testid="ring-unlock-btn"
                >
                  {unlockingRing ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Bell className="h-6 w-6" />
                  )}
                  <span className="font-bold text-lg">
                    {unlockingRing ? 'Ouverture...' : 'Ouvrir l\'interphone'}
                  </span>
                </Button>
              )}
            </div>

            {/* Instruction Photos Section - For housekeepers */}
            {task.property_instruction_photos?.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
                <p className="font-semibold text-purple-800 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Photos d'instructions ({task.property_instruction_photos.filter(p => p).length})
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {task.property_instruction_photos.filter(photo => photo).map((photo, idx) => (
                    <button
                      key={photo || idx}
                      onClick={() => {
                        setShowPhotoPreview(photo);
                      }}
                      className="relative group"
                    >
                      <img 
                        src={`${API_URL}/api/photo?path=${encodeURIComponent(photo)}&auth=${localStorage.getItem('token')}`}
                        alt={`Instruction`}
                        className="w-full h-24 object-cover rounded-lg border border-purple-200 group-hover:opacity-90 transition-opacity"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                        <Eye className="h-6 w-6 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Linen Info - For all users */}
            <div className={`rounded-xl p-4 flex items-center gap-3 ${
              task.property_linen_included 
                ? 'bg-emerald-50 border border-emerald-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <Bed className={`h-6 w-6 ${task.property_linen_included ? 'text-emerald-600' : 'text-amber-600'}`} />
              <div>
                <p className={`font-semibold ${task.property_linen_included ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {task.property_linen_included 
                    ? 'Linge de lit INCLUS' 
                    : 'Linge de lit NON INCLUS (ou en option)'
                  }
                </p>
                <p className={`text-sm ${task.property_linen_included ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {task.property_linen_included 
                    ? 'Bien préparer le logement avec les draps et faire les lits' 
                    : 'À apporter si demandé dans les instructions'
                  }
                </p>
              </div>
            </div>

            {/* Admin only: Répartition linge personnalisée pour les prestations multi-FDM avec linge */}
            {isAdmin && task.property_linen_included && task.housekeeper_ids && task.housekeeper_ids.length >= 2 && (
              <Card className="border-0 shadow-sm bg-indigo-50/50" data-testid="linen-split-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-indigo-800">
                    👕 Répartition linge (multi-FDM)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-600">
                    La FDM qui s'occupe du linge touche un % personnalisé. Les autres se partagent le reste équitablement.
                  </p>

                  {/* Banner auto-détection si une FDM est marquée "ne fait pas le linge" */}
                  {task.linen_split_source === 'auto' && !task.linen_fdm_id && (
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 text-xs text-amber-800" data-testid="linen-auto-banner">
                      ✨ <strong>Répartition automatique appliquée</strong> car une FDM de l'équipe est marquée "ne fait pas le linge".
                      <br />
                      Vous pouvez la conserver telle quelle ou l'écraser manuellement ci-dessous.
                    </div>
                  )}
                  {task.linen_split_source === 'manual' && (
                    <div className="bg-indigo-100 border border-indigo-300 rounded-lg p-2 text-xs text-indigo-800">
                      🛠️ <strong>Répartition manuelle configurée</strong> sur cette prestation.
                    </div>
                  )}

                  {/* Diagnostic banner: aucune règle auto applicable */}
                  {task.linen_split_source === null && task.linen_split_diagnostic && (
                    <div className="bg-slate-100 border border-slate-300 rounded-lg p-2 text-xs text-slate-700" data-testid="linen-diagnostic-banner">
                      ℹ️ <strong>Répartition automatique non appliquée.</strong>
                      <br />
                      {task.linen_split_diagnostic.startsWith('linen_not_included') && (
                        <span>Raison : le logement n'a pas le linge inclus.</span>
                      )}
                      {task.linen_split_diagnostic.startsWith('not_exactly_2_fdm') && (
                        <span>Raison : la prestation doit avoir exactement 2 FDM (actuellement : {task.linen_split_diagnostic.split(':')[1]}).</span>
                      )}
                      {task.linen_split_diagnostic.startsWith('no_fdm_flagged') && (
                        <span>
                          Raison : aucune FDM de l'équipe n'a le flag <em>"Ne fait pas le linge"</em> activé.
                          <br />
                          FDM concernées : <strong>{task.linen_split_diagnostic.split(':').slice(1).join(':')}</strong>.
                          <br />
                          → Ouvrez <em>Équipes</em>, modifiez la FDM qui ne fait pas le linge et cochez la case correspondante.
                        </span>
                      )}
                      {task.linen_split_diagnostic.startsWith('both_fdm_flagged') && (
                        <span>Raison : les <strong>2 FDM</strong> ont le flag "Ne fait pas le linge" — impossible de décider qui touche le supplément. Décochez la case sur une des deux.</span>
                      )}
                    </div>
                  )}

                  {/* Sélecteur FDM */}
                  <div>
                    <Label className="text-xs text-slate-700 mb-1 block">FDM qui s'occupe du linge</Label>
                    <select
                      value={linenFdmId}
                      onChange={(e) => setLinenFdmId(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white"
                      data-testid="linen-fdm-select"
                    >
                      <option value="">— Aucune (50/50 classique) —</option>
                      {task.housekeeper_ids.map((hkId, idx) => (
                        <option key={hkId} value={hkId}>
                          {task.housekeeper_names?.[idx] || hkId}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Pourcentage */}
                  {linenFdmId && (
                    <div>
                      <Label className="text-xs text-slate-700 mb-1 block">
                        Pourcentage pour la FDM linge : <strong>{linenFdmPct}%</strong>
                      </Label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={linenFdmPct}
                        onChange={(e) => setLinenFdmPct(Number(e.target.value))}
                        className="w-full"
                        data-testid="linen-fdm-pct-slider"
                      />
                      <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                        <span>0%</span>
                        <span>50%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  )}

                  {/* Aperçu en direct */}
                  {task.price > 0 && (
                    <div className="bg-white border border-slate-200 rounded-lg p-3 text-xs space-y-1">
                      <p className="font-semibold text-slate-700 mb-1">Aperçu de la répartition :</p>
                      {(() => {
                        const total = Number(task.price || 0);
                        const hkIds = task.housekeeper_ids;
                        const names = task.housekeeper_names || [];
                        const n = hkIds.length;
                        if (!linenFdmId) {
                          const each = total / n;
                          return hkIds.map((hkId, idx) => (
                            <div key={hkId} className="flex justify-between">
                              <span>{names[idx] || hkId}</span>
                              <span className="font-semibold">{each.toFixed(2)}€</span>
                            </div>
                          ));
                        }
                        const linenShare = total * linenFdmPct / 100;
                        const otherShare = (total - linenShare) / (n - 1);
                        return hkIds.map((hkId, idx) => {
                          const isLinen = hkId === linenFdmId;
                          return (
                            <div key={hkId} className={`flex justify-between ${isLinen ? 'text-indigo-700' : ''}`}>
                              <span>{names[idx] || hkId}{isLinen ? ' 👕' : ''}</span>
                              <span className="font-semibold">{(isLinen ? linenShare : otherShare).toFixed(2)}€</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  <Button
                    onClick={handleSaveLinenSplit}
                    disabled={linenSaving}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    data-testid="linen-split-save-btn"
                  >
                    {linenSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Enregistrer la répartition
                  </Button>
                </CardContent>
              </Card>
            )}

            {task.is_urgent && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700 font-medium">Mission urgente - Prioritaire</span>
              </div>
            )}

            {/* Options admin pour la prestation */}
            {task.can_start_early && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-green-600" />
                <span className="text-green-700 font-medium">✅ Peut être fait tôt</span>
              </div>
            )}

            {task.guest_departure_time && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="text-blue-700 font-medium">🚪 Départ voyageurs : {task.guest_departure_time}</span>
              </div>
            )}

            {task.owner_arrival && (
              <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-4 flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
                <span className="text-amber-800 font-bold text-lg">⚠️ {task.owner_arrival}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Control Comment Display */}
        {task.control_comment && (
          <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <MessageSquare className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-blue-700 mb-1">Retour suite au contrôle</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{task.control_comment}</p>
                  
                  {/* Control Comment Photos */}
                  {task.control_comment_photos && task.control_comment_photos.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-500 mb-2">Photos jointes ({task.control_comment_photos.length})</p>
                      <div className="grid grid-cols-3 gap-2">
                        {task.control_comment_photos.map((photo, idx) => (
                          <a
                            key={`control-photo-${photo || idx}`}
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/photo?path=${encodeURIComponent(photo)}&auth=${localStorage.getItem('token')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}/api/photo?path=${encodeURIComponent(photo)}&auth=${localStorage.getItem('token')}`}
                              alt={`Contrôle ${idx + 1}`}
                              className="w-full h-20 object-cover rounded-lg border border-blue-200 hover:border-blue-400 transition-colors cursor-pointer"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {task.control_comment_at && (
                    <p className="text-xs text-gray-400 mt-2">
                      Par {task.control_comment_by_name || 'Admin'} • {(() => { try { return format(new Date(task.control_comment_at), "d MMMM yyyy à HH:mm", { locale: fr }); } catch(e) { return ''; } })()}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Checklist Section - DÉSACTIVÉ TEMPORAIREMENT
        {['en_cours', 'termine', 'probleme'].includes(task.status) && (
          <Card className="border-0 shadow-sm" data-testid="checklist-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-[#003366]" />
                  Checklist de validation
                </div>
                <Badge className={`${checklistComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'} border-0`}>
                  {checklistCompletedCount}/{CHECKLIST_ITEMS.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {CHECKLIST_ITEMS.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <Checkbox
                      id={item.id}
                      checked={checklist[item.id] || false}
                      onCheckedChange={(checked) => handleChecklistChange(item.id, checked)}
                      disabled={task.status !== 'en_cours' || checklistSaving}
                      data-testid={`checklist-${item.id}`}
                    />
                    <label 
                      htmlFor={item.id} 
                      className={`text-sm cursor-pointer ${checklist[item.id] ? 'text-emerald-700 line-through' : 'text-gray-700'}`}
                    >
                      {item.label}
                    </label>
                  </div>
                ))}
              </div>
              {checklistSaving && (
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Sauvegarde...
                </p>
              )}
              {task.status === 'en_cours' && !checklistComplete && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-4">
                  <p className="text-sm text-orange-700">
                    Cochez tous les éléments pour pouvoir valider la prestation
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        */}

        {/* Dish soap verification result (admin/tech/gouvernant view) */}
        {(task.dish_soap_status || isAdmin) && ['en_cours', 'termine', 'probleme', 'rejete'].includes(task.status) && (
          <Card className="border-0 shadow-sm" data-testid="dish-soap-result-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  🧴 Produit vaisselle
                </div>
                {task.dish_soap_penalty ? (
                  <Badge className="bg-red-100 text-red-700 border-0">Pénalité -{task.dish_soap_penalty}€</Badge>
                ) : task.dish_soap_status === 'present' ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-0">Plein</Badge>
                ) : task.dish_soap_status === 'replaced' ? (
                  <Badge className="bg-blue-100 text-blue-700 border-0">Remplacé par la FDM</Badge>
                ) : task.dish_soap_status === 'not_replaced' ? (
                  <Badge className="bg-red-100 text-red-700 border-0">Non remplacé · -2€</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border-0">Non vérifié</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(task.dish_soap_status === 'not_replaced' || task.dish_soap_penalty) && (
                <div className="mb-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-800">
                  ⚠️ Pénalité de 2€ appliquée sur la rémunération de la FDM.
                  {task.dish_soap_admin_justification && (
                    <div className="mt-2 pt-2 border-t border-red-200">
                      <strong>Justification admin {task.dish_soap_admin_set_by_name ? `(${task.dish_soap_admin_set_by_name})` : ''} :</strong>
                      <div className="mt-1 italic">{task.dish_soap_admin_justification}</div>
                    </div>
                  )}
                </div>
              )}
              {task.dish_soap_photo ? (
                <img
                  src={getPhotoUrl(task.dish_soap_photo, token)}
                  alt="Produit vaisselle"
                  className="w-full max-h-72 object-contain rounded-lg border border-slate-200 cursor-pointer"
                  onClick={() => setShowPhotoPreview(task.dish_soap_photo)}
                  data-testid="dish-soap-admin-photo"
                />
              ) : (
                <p className="text-sm italic text-slate-500">
                  La FDM n'a pas vérifié le produit vaisselle pour cette prestation (prestation antérieure au nouveau module ou non validée).
                </p>
              )}
              {/* Admin actions */}
              {isAdmin && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {!task.dish_soap_penalty ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setAdminPenaltyJustification('');
                        setShowAdminPenaltyDialog(true);
                      }}
                      data-testid="admin-apply-penalty-btn"
                    >
                      💸 Appliquer une pénalité -2€
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => {
                          setAdminPenaltyJustification(task.dish_soap_admin_justification || '');
                          setShowAdminPenaltyDialog(true);
                        }}
                        data-testid="admin-edit-penalty-btn"
                      >
                        ✏️ Modifier la justification
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => handleAdminPenalty(false, null)}
                        data-testid="admin-cancel-penalty-btn"
                      >
                        ↩️ Annuler la pénalité
                      </Button>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Photos Section */}
        {['en_cours', 'termine', 'probleme', 'rejete'].includes(task.status) && (
          <Card className="border-0 shadow-sm" data-testid="photos-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-[#003366]" />
                  Photos du ménage
                </div>
                <div className="flex items-center gap-2">
                  {/* Bouton Partager - visible pour Admin/Tech/Gouvernant */}
                  {(user?.role === 'admin' || user?.role === 'technician' || user?.role === 'gouvernant') && uploadedPhotos.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShareGallery}
                      disabled={sharingGallery}
                      className="h-7 text-xs"
                      data-testid="share-gallery-btn"
                    >
                      {sharingGallery ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : linkCopied ? (
                        <>
                          <Check className="h-3 w-3 mr-1 text-green-600" />
                          Copié !
                        </>
                      ) : (
                        <>
                          <Share2 className="h-3 w-3 mr-1" />
                          Partager
                        </>
                      )}
                    </Button>
                  )}
                  <Badge className={`${(uploadedPhotos.length + pendingPhotosCount) >= 5 ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'} border-0`}>
                    {uploadedPhotos.length + pendingPhotosCount}/5 min
                  </Badge>
                </div>
              </CardTitle>
              {/* Afficher le lien si généré */}
              {shareLink && (
                <div className="mt-2 p-2 bg-blue-50 rounded-lg flex items-center gap-2">
                  <Link className="h-4 w-4 text-blue-600 shrink-0" />
                  <input 
                    type="text" 
                    value={shareLink} 
                    readOnly 
                    className="flex-1 text-xs bg-transparent border-0 text-blue-700 outline-none"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={async () => {
                      await navigator.clipboard.writeText(shareLink);
                      setLinkCopied(true);
                      toast.success('Lien copié !');
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                  >
                    {linkCopied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-2">
              {isUploading && (
                <div className="mb-4">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1 text-center">Upload en cours... {uploadProgress}%</p>
                </div>
              )}

              {/* Galerie compacte - Affiche 4 photos max + bouton voir tout */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {/* Photos déjà uploadées sur le serveur (max 4 visibles) */}
                {uploadedPhotos.filter(p => p).slice(0, 4).map((photo, idx) => (
                  <div 
                    key={`uploaded-${photo || idx}`} 
                    className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative cursor-pointer hover:opacity-90 transition-opacity" 
                    onClick={() => setShowPhotoPreview(photo)}
                  >
                    <img src={getPhotoUrl(photo, token)} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    {/* Si c'est la 4ème photo et qu'il y en a plus, afficher le compteur */}
                    {idx === 3 && (uploadedPhotos.filter(p => p).length + pendingPhotoPreviews.length) > 4 && (
                      <div 
                        className="absolute inset-0 bg-black/60 flex items-center justify-center cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); setShowAllPhotos(true); }}
                      >
                        <span className="text-white font-bold text-lg">
                          +{(uploadedPhotos.filter(p => p).length + pendingPhotoPreviews.length) - 4}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Photos en attente (si moins de 4 photos uploadées) */}
                {uploadedPhotos.length < 4 && (pendingPhotoPreviews || []).slice(0, 4 - uploadedPhotos.length).map((pending, idx) => (
                  <div key={`pending-${pending?.id || idx}`} className="aspect-square rounded-lg overflow-hidden bg-orange-100 relative">
                    <img src={pending?.preview || ''} alt={`En attente ${idx + 1}`} className="w-full h-full object-cover opacity-80" onError={(e) => { e.target.style.display = 'none'; }} />
                    <div className="absolute top-1 right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                      <WifiOff className="h-3 w-3" />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bouton Voir toutes les photos si plus de 4 */}
              {((uploadedPhotos || []).length + (pendingPhotoPreviews || []).length) > 4 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mb-3"
                  onClick={() => setShowAllPhotos(true)}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Voir les {(uploadedPhotos || []).length + (pendingPhotoPreviews || []).length} photos
                </Button>
              )}
              
              {/* Bouton d'ajout - TOUJOURS visible quand mission en_cours ou termine avec permission ou ADMIN */}
              {(task.status === 'en_cours' || (task.status === 'termine' && (allowLatePhotos || isAdmin))) && (
                <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-[#003366] rounded-xl cursor-pointer hover:bg-blue-100 transition-colors mb-3 bg-blue-50" data-testid="upload-photo-btn-always">
                  {task.status === 'termine' ? (
                    <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={isUploading} />
                  ) : (
                    <input ref={photoInputRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={handlePhotoUpload} disabled={isUploading} />
                  )}
                  {isUploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#003366]" />
                  ) : (
                    <>
                      <Camera className="h-5 w-5 text-[#003366]" />
                      <span className="text-sm text-[#003366] font-semibold">📸 Ajouter des photos</span>
                    </>
                  )}
                </label>
              )}

              {/* Message pour photos manquantes - en_cours OU termine avec allowLatePhotos/admin */}
              {(uploadedPhotos.length + pendingPhotosCount) < 5 && (task.status === 'en_cours' || (task.status === 'termine' && (allowLatePhotos || isAdmin))) && (
                <div className={`${task.status === 'termine' ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'} border rounded-lg p-3`}>
                  <p className={`text-sm font-medium ${task.status === 'termine' ? 'text-blue-700' : 'text-orange-700'}`}>
                    📸 {task.status === 'termine' ? 'Vous pouvez encore ajouter' : 'Ajoutez encore'} {5 - (uploadedPhotos.length + pendingPhotosCount)} photo(s)
                  </p>
                  <p className={`text-xs mt-1 ${task.status === 'termine' ? 'text-blue-600' : 'text-orange-600'}`}>
                    Photographiez : salon, cuisine, chambres, salles de bain
                  </p>
                </div>
              )}
              
              {/* Message si photos en attente de sync */}
              {pendingPhotosCount > 0 && online && (
                <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 mt-2">
                  <p className="text-sm text-orange-700 font-medium flex items-center gap-2">
                    <CloudUpload className="h-4 w-4" />
                    {pendingPhotosCount} photo(s) en attente de synchronisation
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="mt-2 text-orange-700 border-orange-300 hover:bg-orange-100"
                    onClick={handleSync}
                    disabled={syncing}
                  >
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CloudUpload className="h-4 w-4 mr-2" />}
                    Synchroniser maintenant
                  </Button>
                </div>
              )}

              {(uploadedPhotos.length + pendingPhotosCount) >= 5 && task.status === 'en_cours' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-700 font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    {pendingPhotosCount > 0 ? 'Photos prêtes ! Synchronisez avant de valider.' : 'Prêt à valider la prestation !'}
                  </p>
                </div>
              )}
              
              {/* Message de confirmation pour termine avec allowLatePhotos et photos complètes */}
              {uploadedPhotos.length >= 5 && task.status === 'termine' && allowLatePhotos && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-700 font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Photos ajoutées avec succès !
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Problem Display */}
        {task.status === 'probleme' && task.problem_text && (
          <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-700 mb-1">Problème signalé</p>
                  <p className="text-gray-700">{task.problem_text}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rejection Info */}
        {task.status === 'rejete' && task.rejection_reason && (
          <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-700 mb-1">Prestation refusée</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{task.rejection_reason}</p>
                  {Array.isArray(task.rejection_photos) && task.rejection_photos.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-slate-600 mb-2">
                        {task.rejection_photos.length} photo{task.rejection_photos.length > 1 ? 's' : ''} justificative{task.rejection_photos.length > 1 ? 's' : ''}
                      </p>
                      <div className="flex overflow-x-auto gap-2 pb-1" data-testid="rejection-photos">
                        {task.rejection_photos.map((p, idx) => {
                          const token = localStorage.getItem('token');
                          const url = p.startsWith('http')
                            ? p
                            : `${process.env.REACT_APP_BACKEND_URL}/api/photo?path=${encodeURIComponent(p)}&auth=${token}`;
                          return (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                              <img
                                src={url}
                                alt={`Preuve ${idx + 1}`}
                                className="h-24 w-24 rounded-lg object-cover border border-red-200"
                              />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Non Réalisable Info */}
        {task.status === 'non_realisable' && (
          <Card className="border-0 shadow-sm border-l-4 border-l-red-600 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold text-red-700 mb-2">🚫 Prestation non réalisable</p>
                  <p className="text-red-700 font-medium mb-2">{task.unrealizable_reason_label || 'Raison non spécifiée'}</p>
                  {task.unrealizable_details && (
                    <p className="text-gray-700 mb-2">Détails : {task.unrealizable_details}</p>
                  )}
                  {task.unrealizable_reporter_name && (
                    <p className="text-sm text-gray-500">
                      Signalé par {task.unrealizable_reporter_name} le{' '}
                      {task.unrealizable_reported_at 
                        ? new Date(task.unrealizable_reported_at).toLocaleDateString('fr-FR', { 
                            day: '2-digit', 
                            month: 'long', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          }) 
                        : ''}
                    </p>
                  )}
                  {task.unrealizable_photo && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-600 mb-2">Photo justificative :</p>
                      <img 
                        src={getPhotoUrl(task.unrealizable_photo, token)} 
                        alt="Justificatif" 
                        className="w-full max-w-xs rounded-lg border cursor-pointer hover:opacity-90"
                        onClick={() => setShowPhotoPreview(task.unrealizable_photo)}
                      />
                    </div>
                  )}
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>💰 Impact :</strong> Cette prestation ne sera pas rémunérée.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed Message */}
        {task.status === 'termine' && (
          <Card className="border-0 shadow-sm bg-emerald-50">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-emerald-600" />
              <p className="font-medium text-emerald-700">Prestation terminée</p>
              <p className="text-sm text-emerald-600 mt-1">
                Le paiement de {task.price || 0}€ est comptabilisé dans vos revenus.
              </p>
              {isHousekeeper && (
                <Button 
                  variant="link" 
                  className="text-emerald-700 mt-2 p-0 h-auto"
                  onClick={() => navigate('/app/earnings')}
                >
                  Voir mes revenus →
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin Actions Card */}
        {isTechnicianOrAdmin && !['rejete'].includes(task.status) && (
          <Card className="border-0 shadow-sm bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-[#003366]">Actions administrateur</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 gap-2">
                {/* Admin can force validate tasks that are en_cours */}
                {['en_cours', 'acceptee', 'affectee'].includes(task.status) && (
                  <Button 
                    className="justify-start bg-emerald-600 hover:bg-emerald-700 text-white" 
                    onClick={() => setShowAdminValidateDialog(true)} 
                    data-testid="admin-validate-btn"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Valider la prestation (admin)
                  </Button>
                )}
                {/* PDF Report download - only if task has photos */}
                {uploadedPhotos && uploadedPhotos.length > 0 && (
                  <Button 
                    variant="outline" 
                    className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" 
                    onClick={downloadPdfReport}
                    data-testid="download-pdf-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Télécharger rapport PDF (certificat)
                  </Button>
                )}
                <Button variant="outline" className="justify-start" onClick={() => setShowCommentDialog(true)} data-testid="comment-btn">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Ajouter un commentaire de contrôle
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => {
                  setRescheduleDate(format(addDays(new Date(task.date), 1), 'yyyy-MM-dd'));
                  setShowRescheduleDialog(true);
                }} data-testid="reschedule-btn">
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Changer la date de prestation
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => openResetDialog()} data-testid="reset-btn">
                  <Users className="h-4 w-4 mr-2" />
                  Gérer l'équipe
                </Button>
                {['termine', 'en_cours', 'acceptee', 'affectee'].includes(task.status) && (
                  <Button variant="outline" className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setShowRejectDialog(true)} data-testid="reject-btn">
                    <XCircle className="h-4 w-4 mr-2" />
                    Refuser la prestation
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Action Buttons - Fixed at bottom for housekeepers (Mobile-first design) */}
      {isHousekeeper && !['termine', 'probleme', 'rejete', 'non_realisable'].includes(task.status) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 p-4 shadow-2xl safe-area-bottom">
          {/* Affichage du temps en cours */}
          {task.started_at && !task.ended_at && (
            <div className="mb-3 p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-sm text-blue-600 font-medium">⏱️ Mission en cours depuis</p>
              <p className="text-xl font-bold text-blue-800">
                {task.started_at ? new Date(task.started_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
              </p>
            </div>
          )}

          {/* Temps passé si terminé */}
          {task.time_spent_minutes && (
            <div className="mb-3 p-3 bg-emerald-50 rounded-xl text-center">
              <p className="text-sm text-emerald-600 font-medium">⏱️ Temps passé</p>
              <p className="text-xl font-bold text-emerald-800">{formatTimeSpent(task.time_spent_minutes)}</p>
            </div>
          )}

          {/* Étape 1: Accepter */}
          {task.status === 'affectee' && (
            <Button 
              className="w-full h-16 text-lg bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold" 
              onClick={handleAccept} 
              disabled={actionLoading} 
              data-testid="accept-mission-btn"
            >
              {actionLoading ? <Loader2 className="h-6 w-6 animate-spin mr-3" /> : <CheckCircle2 className="h-6 w-6 mr-3" />}
              ACCEPTER LA MISSION
            </Button>
          )}

          {/* Étape 2: J'arrive (Pointage arrivée) */}
          {task.status === 'acceptee' && (
            <div className="space-y-3">
              <Button 
                className="w-full h-16 text-lg bg-purple-600 hover:bg-purple-700 rounded-xl font-semibold" 
                onClick={handleStart} 
                disabled={actionLoading} 
                data-testid="start-mission-btn"
              >
                {actionLoading ? <Loader2 className="h-6 w-6 animate-spin mr-3" /> : <Play className="h-6 w-6 mr-3" />}
                📍 JE SUIS ARRIVÉ(E)
              </Button>
              
              {/* Bouton Non Réalisable - visible après acceptation */}
              <Button 
                variant="outline"
                className="w-full h-14 text-base border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 rounded-xl font-medium" 
                onClick={() => setShowUnrealizableDialog(true)} 
                disabled={actionLoading} 
                data-testid="unrealizable-btn"
              >
                <XCircle className="h-5 w-5 mr-2" />
                🚫 Prestation non réalisable
              </Button>
            </div>
          )}

          {/* Étape 3: En cours - Photos + Validation */}
          {task.status === 'en_cours' && (
            <div className="space-y-3">
              {/* Barre de progression - masquée si allow_late_photos et pas de photos */}
              {(!allowLatePhotos || uploadedPhotos.length > 0 || pendingPhotosCount > 0) && (
                <div className="flex items-center justify-center text-sm text-gray-600 mb-2">
                  <span>📷 Photos: {uploadedPhotos.length + pendingPhotosCount}/5</span>
                  {pendingPhotosCount > 0 && (
                    <span className="ml-2 text-orange-600">
                      ({pendingPhotosCount} hors-ligne)
                    </span>
                  )}
                </div>
              )}
              
              {/* Info pour utilisateurs avec allow_late_photos */}
              {allowLatePhotos && (uploadedPhotos.length + pendingPhotosCount) < 5 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <p className="text-blue-700 text-sm">
                    📸 Vous pouvez valider sans photos et les ajouter plus tard
                  </p>
                </div>
              )}

              {/* Raccourci produit vaisselle — accessible à tout moment pendant la prestation */}
              <button
                type="button"
                onClick={() => setShowDishSoapDialog(true)}
                className={`w-full flex items-center justify-between gap-2 p-3 rounded-xl border-2 text-sm font-semibold transition ${
                  dishSoapStatus && dishSoapPhoto
                    ? (dishSoapStatus === 'not_replaced'
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : 'bg-emerald-50 border-emerald-300 text-emerald-700')
                    : 'bg-orange-50 border-orange-400 text-orange-800 animate-pulse'
                }`}
                data-testid="dish-soap-quick-btn"
              >
                <span>🧴 Produit vaisselle</span>
                <span>
                  {dishSoapStatus && dishSoapPhoto
                    ? (dishSoapStatus === 'not_replaced' ? '⚠️ -2€' : '✓ Vérifié')
                    : '⚠️ À vérifier'}
                </span>
              </button>

              {/* Bouton de validation finale */}
              <Button 
                className={`w-full h-16 text-lg rounded-xl font-semibold ${
                  allowLatePhotos || (uploadedPhotos.length + pendingPhotosCount) >= 5
                    ? 'bg-emerald-600 hover:bg-emerald-700' 
                    : 'bg-gray-400'
                }`}
                onClick={() => {
                  // Vérifier le produit vaisselle avant toute validation
                  if (!dishSoapStatus || !dishSoapPhoto) {
                    setShowDishSoapDialog(true);
                    return;
                  }
                  if (allowLatePhotos && (uploadedPhotos.length + pendingPhotosCount) < 5) {
                    // Ouvrir le dialog de sélection d'heure
                    setShowCustomTimeDialog(true);
                  } else {
                    handleComplete();
                  }
                }}
                disabled={actionLoading || (!allowLatePhotos && (uploadedPhotos.length + pendingPhotosCount) < 5)}
                data-testid="complete-mission-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin mr-3" />
                ) : (
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                )}
                {((uploadedPhotos.length + pendingPhotosCount) < 5 && !allowLatePhotos)
                  ? `📷 Ajoutez ${5 - (uploadedPhotos.length + pendingPhotosCount)} photo(s)`
                  : allowLatePhotos && (uploadedPhotos.length + pendingPhotosCount) < 5
                    ? "✅ TERMINER (photos plus tard)"
                    : pendingPhotosCount > 0 
                      ? "📤 SYNCHRONISER ET TERMINER"
                      : (dishSoapStatus === 'not_replaced' ? "✅ J'AI TERMINÉ (-2€)" : "✅ J'AI TERMINÉ")
                }
              </Button>
              
              {/* Bouton Non Réalisable - visible aussi quand en cours */}
              <Button 
                variant="outline"
                className="w-full h-12 text-sm border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 rounded-xl font-medium" 
                onClick={() => setShowUnrealizableDialog(true)} 
                disabled={actionLoading} 
                data-testid="unrealizable-en-cours-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                🚫 Prestation non réalisable
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Photo Preview Dialog */}
      <Dialog open={!!showPhotoPreview} onOpenChange={() => setShowPhotoPreview(null)}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
          {showPhotoPreview && <img src={getPhotoUrl(showPhotoPreview, token)} alt="Preview" className="w-full h-auto" onError={(e) => { e.target.src = ''; }} />}
        </DialogContent>
      </Dialog>

      {/* All Photos Gallery Dialog */}
      <Dialog open={showAllPhotos} onOpenChange={setShowAllPhotos}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Toutes les photos ({uploadedPhotos.length + pendingPhotoPreviews.length})
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {uploadedPhotos.filter(p => p).map((photo, idx) => (
              <div 
                key={`all-photo-${photo || idx}`} 
                className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => { setShowAllPhotos(false); setShowPhotoPreview(photo); }}
              >
                <img src={getPhotoUrl(photo, token)} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="absolute top-1 right-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">{idx + 1}</div>
              </div>
            ))}
            {(pendingPhotoPreviews || []).map((pending, idx) => (
              <div key={`pending-all-${pending?.id || idx}`} className="aspect-square rounded-lg overflow-hidden bg-orange-100 relative">
                <img src={pending?.preview || ''} alt={`En attente ${idx + 1}`} className="w-full h-full object-cover opacity-80" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="absolute top-1 right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                  <WifiOff className="h-3 w-3" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-orange-500/90 text-white text-xs py-1 text-center">
                  En attente
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Keybox Photo Dialog - Required before validation */}
      <KeyboxPhotoDialog
        open={showKeyboxPhotoDialog}
        onOpenChange={setShowKeyboxPhotoDialog}
        keyboxPhoto={keyboxPhoto}
        keyboxPhotoUploading={keyboxPhotoUploading}
        onCapture={handleKeyboxPhotoCapture}
        onConfirm={handleKeyboxPhotoConfirmed}
        token={token}
      />

      {/* Dish Soap Verification Dialog */}
      <Dialog open={showDishSoapDialog} onOpenChange={setShowDishSoapDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🧴 Produit vaisselle
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-700">
              Vérifiez le produit vaisselle dans le logement et envoyez une photo. <strong>Obligatoire pour valider.</strong>
            </p>

            {/* Choix : 3 options */}
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => setDishSoapStatus('present')}
                className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold transition text-left ${
                  dishSoapStatus === 'present'
                    ? 'bg-emerald-600 text-white border-emerald-700'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'
                }`}
                data-testid="dish-soap-present-btn"
              >
                ✅ Plein, rien à remplacer
              </button>
              <button
                type="button"
                onClick={() => setDishSoapStatus('replaced')}
                className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold transition text-left ${
                  dishSoapStatus === 'replaced'
                    ? 'bg-blue-600 text-white border-blue-700'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                }`}
                data-testid="dish-soap-replaced-btn"
              >
                🔄 J'ai remplacé le flacon
              </button>
              <button
                type="button"
                onClick={() => setDishSoapStatus('not_replaced')}
                className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold transition text-left ${
                  dishSoapStatus === 'not_replaced'
                    ? 'bg-red-600 text-white border-red-700'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-red-400'
                }`}
                data-testid="dish-soap-not-replaced-btn"
              >
                ⚠️ Fond / vide, je ne remplace pas (-2€)
              </button>
            </div>

            {/* Avertissement si "not_replaced" */}
            {dishSoapStatus === 'not_replaced' && (
              <div className="bg-red-100 border border-red-300 rounded-lg p-3" data-testid="dish-soap-penalty-warning">
                <p className="text-red-900 text-sm font-semibold mb-1">
                  ⚠️ Attention : 2€ seront déduits de votre rémunération.
                </p>
                <p className="text-red-800 text-xs">
                  Le produit vaisselle est essentiel pour les voyageurs.
                </p>
                <button
                  type="button"
                  onClick={() => setDishSoapStatus('replaced')}
                  className="mt-2 text-xs font-semibold text-blue-700 underline"
                  data-testid="dish-soap-change-mind-btn"
                >
                  🔄 Je remplace finalement (annule la pénalité)
                </button>
              </div>
            )}

            {/* Photo upload / preview */}
            {dishSoapStatus && (
              <div>
                <p className="text-xs text-slate-700 mb-2 font-medium">
                  {dishSoapStatus === 'present'
                    ? "📸 Photo du flacon plein :"
                    : dishSoapStatus === 'replaced'
                      ? "📸 Photo du nouveau flacon de remplacement :"
                      : "📸 Photo du flacon (fond / vide) :"}
                </p>

                {dishSoapPhoto ? (
                  <div className="space-y-2">
                    <img
                      src={getPhotoUrl(dishSoapPhoto, token)}
                      alt="Produit vaisselle"
                      className="w-full max-h-48 object-cover rounded-lg border border-slate-300"
                      data-testid="dish-soap-photo-preview"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => dishSoapInputRef.current?.click()}
                      disabled={dishSoapUploading}
                      data-testid="dish-soap-retake-btn"
                    >
                      {dishSoapUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                      Reprendre la photo
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => dishSoapInputRef.current?.click()}
                    disabled={dishSoapUploading}
                    data-testid="dish-soap-take-photo-btn"
                  >
                    {dishSoapUploading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Camera className="h-5 w-5 mr-2" />}
                    Prendre la photo
                  </Button>
                )}

                <input
                  ref={dishSoapInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleDishSoapPhotoCapture}
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDishSoapDialog(false)}
              data-testid="dish-soap-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!dishSoapStatus || !dishSoapPhoto}
              onClick={() => {
                setShowDishSoapDialog(false);
                // Relance la complétion automatiquement
                if (allowLatePhotos && (uploadedPhotos.length + pendingPhotosCount) < 5) {
                  setShowCustomTimeDialog(true);
                } else {
                  setTimeout(() => handleComplete(), 50);
                }
              }}
              data-testid="dish-soap-confirm-btn"
            >
              {dishSoapStatus === 'not_replaced' ? "✓ Valider (-2€)" : "✓ Valider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin : appliquer / modifier la pénalité produit vaisselle */}
      <Dialog open={showAdminPenaltyDialog} onOpenChange={setShowAdminPenaltyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              💸 Appliquer la pénalité produit vaisselle (-2€)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-700">
              Indiquez la raison de la pénalité. <strong>Cette justification sera visible par la FDM</strong> dans sa fiche de prestation et sur son relevé de paiement.
            </p>
            <Textarea
              value={adminPenaltyJustification}
              onChange={(e) => setAdminPenaltyJustification(e.target.value)}
              placeholder="Ex : Flacon rempli avec de l'eau au lieu du produit vaisselle, constaté par signalement voyageur du JJ/MM/AAAA."
              rows={4}
              className="text-sm"
              data-testid="admin-penalty-justification-input"
            />
            <p className="text-[11px] text-slate-500 italic">
              Conformément aux Conditions d'utilisation (article Fournitures &amp; Pénalités), une retenue forfaitaire de 2€ sera appliquée sur la rémunération de cette prestation.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAdminPenaltyDialog(false)}
              disabled={adminPenaltySaving}
              data-testid="admin-penalty-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={!adminPenaltyJustification.trim() || adminPenaltySaving}
              onClick={() => handleAdminPenalty(true, adminPenaltyJustification.trim())}
              data-testid="admin-penalty-confirm-btn"
            >
              {adminPenaltySaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirmer -2€
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GPS Permission Modal */}
      <GPSPermissionModal
        open={showGPSModal}
        onClose={() => setShowGPSModal(false)}
        onSuccess={handleGPSSuccess}
        onError={() => setShowGPSModal(false)}
      />

      {/* Post-Validation Dialog - Report or Complete */}
      <Dialog open={showPostValidationDialog} onOpenChange={setShowPostValidationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">
              Validation de la prestation
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 space-y-4">
            <p className="text-center text-gray-600">
              Y a-t-il quelque chose à signaler concernant ce logement ?
            </p>
            <p className="text-center text-sm text-gray-500">
              (Dégâts, objets manquants, problème de propreté à l'arrivée, etc.)
            </p>
            
            <div className="flex flex-col gap-3 pt-4">
              <Button 
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleFinalComplete(false)}
                disabled={actionLoading}
                data-testid="nothing-to-report-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                )}
                RIEN A SIGNALER
              </Button>
              
              <Button 
                className="w-full h-14 text-lg bg-red-600 hover:bg-red-700"
                onClick={() => handleFinalComplete(true)}
                disabled={actionLoading}
                data-testid="report-issue-btn"
              >
                <AlertTriangle className="h-5 w-5 mr-2" />
                SIGNALER UN PROBLÈME
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Problem Dialog */}
      <ProblemReportDialog
        open={showProblemDialog}
        onOpenChange={setShowProblemDialog}
        problemText={problemText}
        setProblemText={setProblemText}
        problemPhotos={problemPhotos}
        onPhotoUpload={handleProblemPhotoUpload}
        onSubmit={handleProblemAndComplete}
        onBack={() => { setShowProblemDialog(false); setShowPostValidationDialog(true); }}
        isUploading={isUploading}
        actionLoading={actionLoading}
        token={token}
      />

      {/* Control Comment Dialog */}
      <ControlCommentDialog
        open={showCommentDialog}
        onOpenChange={setShowCommentDialog}
        controlComment={controlComment}
        setControlComment={setControlComment}
        controlRating={controlRating}
        setControlRating={setControlRating}
        controlPhotos={controlPhotos}
        setControlPhotos={setControlPhotos}
        controlPhotosPreviews={controlPhotosPreviews}
        setControlPhotosPreviews={setControlPhotosPreviews}
        onSubmit={handleControlComment}
        actionLoading={actionLoading}
      />

      {/* Reject Dialog */}
      <RejectDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        photos={rejectPhotos}
        setPhotos={setRejectPhotos}
        onReject={handleReject}
        actionLoading={actionLoading}
      />

      {/* Admin Validate Dialog */}
      <AdminValidateDialog
        open={showAdminValidateDialog}
        onOpenChange={setShowAdminValidateDialog}
        task={task}
        uploadedPhotos={uploadedPhotos}
        adminPhotos={adminPhotos}
        setAdminPhotos={setAdminPhotos}
        adminPhotosPreviews={adminPhotosPreviews}
        setAdminPhotosPreviews={setAdminPhotosPreviews}
        onPhotoSelect={handleAdminPhotoSelect}
        onValidate={handleAdminValidate}
        actionLoading={actionLoading}
        adminPhotoUploading={adminPhotoUploading}
        arrivalTime={adminArrivalTime}
        setArrivalTime={setAdminArrivalTime}
        departureTime={adminDepartureTime}
        setDepartureTime={setAdminDepartureTime}
      />

      {/* Reset/Reassign Dialog */}
      <ReassignDialog
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
        task={task}
        availableHousekeepers={availableHousekeepers}
        loadingHousekeepers={loadingHousekeepers}
        selectedNewHousekeepers={selectedNewHousekeepers}
        toggleHousekeeperSelection={toggleHousekeeperSelection}
        onReassign={handleReassign}
        actionLoading={actionLoading}
      />

      {/* Early Completion Dialog — Facturation au temps réel */}
      <Dialog open={showEarlyCompletionDialog} onOpenChange={(open) => { if (!open) { setShowEarlyCompletionDialog(false); setEarlyCompletionInfo(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="early-completion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>⏱️ Vous avez fini en avance</span>
            </DialogTitle>
            <DialogDescription>
              {earlyCompletionInfo?.message}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2 my-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Temps prévu</span>
              <span className="font-semibold">{earlyCompletionInfo?.expected_minutes} min</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Temps réel sur place</span>
              <span className="font-semibold">{earlyCompletionInfo?.actual_minutes} min</span>
            </div>
            <div className="border-t border-amber-300 pt-2 mt-2 flex justify-between">
              <span className="text-slate-700 font-medium">Il vous reste</span>
              <span className="font-bold text-amber-800">{earlyCompletionInfo?.remaining_minutes} min</span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Prix prévu</span>
              <span className="font-semibold line-through text-slate-400">{earlyCompletionInfo?.original_price} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-700 font-medium">Si vous validez maintenant</span>
              <span className="font-bold text-purple-700">{earlyCompletionInfo?.adjusted_price} €</span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Facturé au tarif horaire de {earlyCompletionInfo?.hourly_rate}€/h avec un plancher à {Math.round((earlyCompletionInfo?.min_floor_pct || 0) * 100)}% du prix initial.
            </p>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <button
              type="button"
              onClick={() => { setShowEarlyCompletionDialog(false); setEarlyCompletionInfo(null); }}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition"
              disabled={actionLoading}
              data-testid="early-completion-stay-btn"
            >
              ← Je reste et je continue le ménage
            </button>
            <button
              type="button"
              onClick={handleConfirmEarlyCompletion}
              disabled={actionLoading}
              className="w-full bg-white border-2 border-slate-300 hover:border-purple-400 text-slate-700 font-medium py-3 rounded-xl transition disabled:opacity-50"
              data-testid="early-completion-confirm-btn"
            >
              {actionLoading ? 'Validation…' : "J'ai vraiment fini — Valider à " + (earlyCompletionInfo?.adjusted_price || '?') + " €"}
            </button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Add Team Member Dialog (sans reset) */}
      <Dialog open={showAddTeamMemberDialog} onOpenChange={setShowAddTeamMemberDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-500" />
              Ajouter un membre à l'équipe
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Équipe actuelle */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-sm font-medium text-blue-800 mb-2">Équipe actuelle :</p>
              <div className="flex flex-wrap gap-2">
                {task?.housekeeper_names?.map((name, idx) => (
                  <Badge key={idx} className="bg-blue-100 text-blue-700">
                    <Users className="h-3 w-3 mr-1" />
                    {name}
                  </Badge>
                )) || (
                  <Badge className="bg-blue-100 text-blue-700">
                    <Users className="h-3 w-3 mr-1" />
                    {task?.housekeeper_name || 'Non assigné'}
                  </Badge>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-600">
              Sélectionnez une FDM à ajouter à cette équipe (maximum 2 personnes) :
            </p>

            {loadingHousekeepers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : availableHousekeepers.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                Aucune autre FDM disponible
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableHousekeepers.map(hk => (
                  <button
                    key={hk.id}
                    type="button"
                    onClick={() => toggleHousekeeperSelection(hk)}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                      selectedNewHousekeepers.some(h => h.id === hk.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`add-team-hk-${hk.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{hk.name}</p>
                        <p className="text-sm text-gray-500">{hk.email}</p>
                      </div>
                      {selectedNewHousekeepers.some(h => h.id === hk.id) && (
                        <CheckCircle2 className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {selectedNewHousekeepers.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-sm font-medium text-emerald-800">
                  + {selectedNewHousekeepers.map(h => h.name).join(', ')}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddTeamMemberDialog(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleAddTeamMember}
              disabled={actionLoading || selectedNewHousekeepers.length === 0}
              data-testid="confirm-add-team-btn"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Ajout en cours...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Ajouter à l'équipe
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <RescheduleDialog
        open={showRescheduleDialog}
        onOpenChange={setShowRescheduleDialog}
        task={task}
        rescheduleDate={rescheduleDate}
        setRescheduleDate={setRescheduleDate}
        rescheduleReason={rescheduleReason}
        setRescheduleReason={setRescheduleReason}
        onReschedule={handleReschedule}
        actionLoading={actionLoading}
      />

      {/* Custom Time Dialog (for allow_late_photos users) */}
      <Dialog open={showCustomTimeDialog} onOpenChange={setShowCustomTimeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              Heure de fin de prestation
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Sélectionnez l'heure à laquelle vous avez terminé la prestation (entre 10h et 16h).
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-amber-700 text-sm">
                ⚠️ Vous n'avez pas encore ajouté de photos. Vous pourrez les ajouter ultérieurement.
              </p>
            </div>
            <div className="flex items-center gap-4 justify-center">
              <div className="space-y-2">
                <Label className="text-center block">Heure</Label>
                <select
                  className="w-24 h-12 text-2xl font-bold text-center border rounded-xl"
                  value={customEndHour}
                  onChange={(e) => setCustomEndHour(parseInt(e.target.value))}
                  data-testid="custom-hour-select"
                >
                  {[10, 11, 12, 13, 14, 15, 16].map(h => (
                    <option key={h} value={h}>{h}h</option>
                  ))}
                </select>
              </div>
              <span className="text-3xl font-bold mt-6">:</span>
              <div className="space-y-2">
                <Label className="text-center block">Minutes</Label>
                <select
                  className="w-24 h-12 text-2xl font-bold text-center border rounded-xl"
                  value={customEndMinute}
                  onChange={(e) => setCustomEndMinute(parseInt(e.target.value))}
                  data-testid="custom-minute-select"
                >
                  {[0, 15, 30, 45].map(m => (
                    <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Question signalement */}
            <div className="border-t pt-4 mt-4">
              <p className="text-center text-gray-700 font-medium mb-3">
                Y a-t-il quelque chose à signaler ?
              </p>
              <p className="text-center text-xs text-gray-500 mb-4">
                (Dégâts, objets manquants, problème de propreté à l'arrivée...)
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
              onClick={async () => {
                setActionLoading(true);
                try {
                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                  const response = await fetch(`${API_URL}/api/tasks/${id}/complete-custom-time`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      end_hour: customEndHour,
                      end_minute: customEndMinute
                    })
                  });
                  const data = await response.json();
                  if (response.ok) {
                    toast.success(data.message || 'Prestation validée !');
                    setShowCustomTimeDialog(false);
                    fetchTask();
                  } else {
                    toast.error(data.detail || 'Erreur lors de la validation');
                  }
                } catch (error) {
                  toast.error('Erreur de connexion');
                } finally {
                  setActionLoading(false);
                }
              }}
              disabled={actionLoading}
              data-testid="custom-time-confirm-btn"
            >
              {actionLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
              RIEN À SIGNALER
            </Button>
            <Button 
              className="w-full h-12 bg-red-600 hover:bg-red-700"
              onClick={() => {
                setShowCustomTimeDialog(false);
                setShowProblemDialog(true);
              }}
              disabled={actionLoading}
              data-testid="custom-time-problem-btn"
            >
              <AlertTriangle className="h-5 w-5 mr-2" />
              SIGNALER UN PROBLÈME
            </Button>
            <Button variant="outline" className="w-full" onClick={() => setShowCustomTimeDialog(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Non Réalisable Dialog */}
      <Dialog open={showUnrealizableDialog} onOpenChange={setShowUnrealizableDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Prestation non réalisable
            </DialogTitle>
          </DialogHeader>
          
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto py-4 space-y-4 -mx-6 px-6">
            <div className="bg-red-100 border-2 border-red-300 rounded-xl p-4">
              <p className="text-red-800 text-sm font-medium">
                ⚠️ <strong>Attention :</strong> En signalant cette prestation comme non réalisable, 
                vous ne serez pas rémunéré(e) pour cette mission. L'administrateur sera notifié par email.
              </p>
            </div>

            {/* Reason selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Sélectionnez la raison :</Label>
              
              <div className="grid gap-2">
                {[
                  { value: 'no_keys', label: '🔑 Pas de clés / Boîte à clés vide', icon: Key },
                  { value: 'guests_present', label: '👥 Voyageurs encore sur place', icon: Users },
                  { value: 'inaccessible', label: '🚫 Logement inaccessible', icon: Building },
                  { value: 'other', label: '📝 Autre raison', icon: MessageSquare }
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setUnrealizableReason(option.value)}
                    className={`w-full p-3 rounded-xl border-2 text-left flex items-center gap-3 transition-all ${
                      unrealizableReason === option.value
                        ? 'border-red-500 bg-red-100 text-red-700'
                        : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
                    }`}
                  >
                    <span className="text-lg">{option.label.split(' ')[0]}</span>
                    <span className="font-medium">{option.label.split(' ').slice(1).join(' ')}</span>
                    {unrealizableReason === option.value && (
                      <CheckCircle2 className="h-5 w-5 ml-auto text-red-500" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Details for "other" reason */}
            {unrealizableReason === 'other' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Décrivez le problème :</Label>
                <Textarea
                  placeholder="Ex: La serrure est cassée, impossible d'entrer..."
                  value={unrealizableDetails}
                  onChange={(e) => setUnrealizableDetails(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="unrealizable-details-input"
                />
              </div>
            )}

            {/* Optional details for other reasons too */}
            {unrealizableReason && unrealizableReason !== 'other' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Détails supplémentaires (optionnel) :</Label>
                <Textarea
                  placeholder="Ajoutez des précisions si nécessaire..."
                  value={unrealizableDetails}
                  onChange={(e) => setUnrealizableDetails(e.target.value)}
                  rows={2}
                  className="resize-none"
                  data-testid="unrealizable-extra-details-input"
                />
              </div>
            )}

            {/* Photo upload */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Photo justificative (recommandé) :</Label>
              <p className="text-xs text-gray-500">Une photo aidera l'admin à comprendre la situation</p>
              
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={unrealizablePhotoRef}
                onChange={handleUnrealizablePhotoSelect}
                className="hidden"
              />
              
              {unrealizablePhotoPreview ? (
                <div className="relative">
                  <img 
                    src={unrealizablePhotoPreview} 
                    alt="Justificatif" 
                    className="w-full h-32 object-cover rounded-xl border"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setUnrealizablePhoto(null);
                      setUnrealizablePhotoPreview(null);
                    }}
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-20 border-dashed border-2"
                  onClick={() => unrealizablePhotoRef.current?.click()}
                >
                  <div className="flex flex-col items-center text-gray-500">
                    <Camera className="h-6 w-6 mb-1" />
                    <span className="text-sm">Prendre une photo</span>
                  </div>
                </Button>
              )}
            </div>
          </div>
          
          {/* Fixed footer buttons */}
          <div className="flex-shrink-0 flex flex-col gap-2 pt-4 border-t">
            <Button 
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
              onClick={handleUnrealizable}
              disabled={actionLoading || !unrealizableReason || (unrealizableReason === 'other' && !unrealizableDetails.trim())}
              data-testid="confirm-unrealizable-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <XCircle className="h-5 w-5 mr-2" />
              )}
              Confirmer : Non réalisable
            </Button>
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => {
                setShowUnrealizableDialog(false);
                setUnrealizableReason('');
                setUnrealizableDetails('');
                setUnrealizablePhoto(null);
                setUnrealizablePhotoPreview(null);
              }}
            >
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wrapper avec Error Boundary
const TaskDetailWithErrorBoundary = () => (
  <TaskErrorBoundary>
    <TaskDetail />
  </TaskErrorBoundary>
);

export default TaskDetailWithErrorBoundary;
